import { ID, uuid } from '../../_metronic/helpers/crud-helper/models';
import { useQuery, useMutation, useQueryClient, QueryClient } from 'react-query';
import axios, { AxiosResponse } from 'axios';
import fileDownload from 'js-file-download';

import {
  Service,
  ProviderService,
  ProviderTimeSlot,
  ProviderTimeSlotService,
  Provider,
  Client,
  Guardian,
  TimeSlot,
  Event,
  ClientProvider,
  ClientOnboarding,
  AgeGroup,
  Language,
  Expertise,
  Concern,
  IWizForm,
  IWizFormTemplate,
  IWizFormTemplateExists,
  Note,
  Document,
  Clinic,
  PostalCode,
  ClientInvite,
  EventLog,
  IInvoice,
  IIcsIntegration,
  ClientSubscription,
  INoteTemplate,
  IProviderByInvite,
  IExpense,
  ISuperBill,
  Dataset,
  Location,
  ServicePlanRow,
} from './models';
import { AvailabilityList, AvailableTimeSlot } from '../modules/apps/booking/core/_models';
import { ClientOneOffPayment } from '../modules/apps/clients/core/_models';
import { useAuth } from '../modules/auth';
import queryString from 'query-string';
import qs from 'qs';
import { monitoringCaptureError } from './monitoring';
import { baseDomain } from './funcs';
import moment from 'moment';

const get_api_url = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (location.hostname === 'localhost') return `http://${baseDomain}:8000/wizapi/v42`;
  if (location.hostname.includes('sta')) return `https://api-staging.${baseDomain}/wizapi/v42`;
  return `https://api.${baseDomain}/wizapi/v42`;
};

export const API_URL = get_api_url();

//export const GET_SERVICES_FOR_SLOT_URL = (slotId: uuid) => `${API_URL}/stafftimeslot/${slotId}/services/`;
export const GET_PROVIDER_SERVICES_URL = (timeslot: boolean) =>
  `${API_URL}/provider-services/?timeslot__isnull=${!timeslot}`;
export const GET_PROVIDER_SERVICE_URL = (uuid: uuid) => `${API_URL}/provider-services/${uuid}/`;
export const PROVIDER_SERVICES_CAN_BOOK_EXISTING_URL = `${API_URL}/provider-services/can-book-existing/`;
export const PROVIDER_SERVICE_CREATE_URL = `${API_URL}/provider-services/`;
export const CLIENT_URL = `${API_URL}/clients/`;
export const GUARDIAN_URL = `${API_URL}/guardians/`;
export const CLIENT_PROVIDER_URL = `${API_URL}/client-providers/`;
export const CLIENT_SUBSCRIPTIONS_URL = `${API_URL}/client-subscriptions/`;
export const CLIENT_INVITES_URL = `${API_URL}/client-invites/`;
export const PROVIDER_URL = `${API_URL}/providers/`;
export const CLINIC_PROVIDER_URL = `${API_URL}/providers/clinic/`;
export const PROVIDER_CONNECTIONS_URL = `${API_URL}/provider-connections/`;
export const DATASETS_URL = (type: string) => `${API_URL}/datasets/${type}/`;
export const LOCATIONS_URL = `${API_URL}/locations/`;
//export const PROVIDER_PROVIDER_URL = `${API_URL}/provider-providers/`;
export const SERVICE_TIMESLOT_URL = (service_uuid: uuid) => `${API_URL}/services/${service_uuid}/timeslots/`;
export const PROVIDER_SERVICE_TIMESLOT_URL = (provider_service_uuid: uuid) => `${API_URL}/provider-services/${provider_service_uuid}/timeslots/`;
export const FORM_TEMPLATE_URL = (name_or_uuid: uuid | string) => `${API_URL}/form-templates/${name_or_uuid}/`;
export const FORM_TEMPLATES_URL = `${API_URL}/form-templates/`;
export const FORM_URL = `${API_URL}/forms/`;
export const CLINIC_URL = `${API_URL}/clinics/`;
export const PROVIDER_REFERRALS_URL = (uuid: uuid) => `${API_URL}/providers/${uuid}/referrals/`;
export const REFERRALS_URL = `${API_URL}/referrals/`;
export const CLIENT_CONTACT_URL = (uuid: ID) => `${API_URL}/clients/${uuid}/contacts/`;

function useServices(onError?: (error?: any) => void) {
  return useQuery(
    'services',
    async (): Promise<Array<Service>> => {
      const { data } = await axios.get(`${API_URL}/services/`);
      return data;
    },
    {
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useServices',
        });
        if (onError) onError(error);
        else throw error;
      },
      refetchOnWindowFocus: false,
    }
  );
}

function useService(uuid?: uuid, onError?: (error?: any) => void) {
  return useQuery(
    ['service', uuid],
    async (): Promise<Service> => {
      const { data } = await axios.get(`${API_URL}/services/${uuid}/`);
      return data;
    },
    {
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useService',
          data: { uuid },
        });
        if (onError) onError(error);
        else throw error;
      },
      enabled: !!uuid,
    }
  );
}

// WST-754: plain getter behind useProviderServices, for imperative lookups (bill dialog provider switch).
async function getProviderServicesForProvider(provider: Provider, withTimeslot?: any): Promise<Array<ProviderService>> {
  const { data } = await axios.get(
    `${API_URL}/providers/${provider.uuid}/services/?timeslot__isnull=${!withTimeslot}`
  );
  return data;
}

function useProviderServices(props?: {
  distinct?: boolean;
  withTimeslot?: any;
  enabled?: boolean;
  provider?: Provider;
  onError?: (error?: any) => void;
}) {
  return useQuery(
    // distinct/withTimeslot change the response (distinct collapses all custom services into one row).
    ['provider-services', props?.provider?.uuid, !!props?.distinct, !!props?.withTimeslot],
    async (): Promise<Array<ProviderService>> => {
      if (props?.provider) return getProviderServicesForProvider(props.provider, props.withTimeslot);
      const { data } = await axios.get(
        `${API_URL}/provider-services/?timeslot__isnull=${!props?.withTimeslot}&distinct=${!!props?.distinct}`
      );
      return data;
    },
    {
      enabled: props?.enabled !== false,
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useService',
          data: { distinct: props?.distinct, withTimeslot: props?.withTimeslot, enabled: props?.enabled },
        });
        if (props?.onError) props?.onError(error);
        else throw error;
      },
      refetchOnWindowFocus: false,
    }
  );
}

function getProviderService(uuid: uuid, children?: boolean) {
  return axios
    .get(`${API_URL}/provider-services/${uuid}${children ? '?children=true' : ''}`)
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'getProviderService',
        data: { uuid },
      });
      throw error;
    });
}

function getExistingClientBookingAccess() {
  return axios
    .get(PROVIDER_SERVICES_CAN_BOOK_EXISTING_URL)
    .then((res) => {
      return !!res.data?.can_book_existing_client;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'getExistingClientBookingAccess',
      });
      throw error;
    });
}

function useExistingClientBookingAccess(props?: { enabled?: boolean; onError?: (error?: any) => void }) {
  const { currentUser } = useAuth();
  return useQuery(
    ['existing-client-booking-access'],
    async (): Promise<boolean> => {
      return await getExistingClientBookingAccess();
    },
    {
      enabled: props?.enabled !== false && currentUser !== undefined,
      refetchOnWindowFocus: false,
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useExistingClientBookingAccess',
        });
        if (props?.onError) props?.onError(error);
        else throw error;
      },
    }
  );
}

function useProviderService(uuid?: uuid, children?: boolean, onError?: (error?: any) => void) {
  return useQuery(
    ['provider-service', uuid],
    async (): Promise<ProviderService> => {
      return await getProviderService(uuid, children);
    },
    {
      enabled: !!uuid,
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useProviderService',
          data: { uuid, children },
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

function getProviderServiceByProviderAndService(provider: Provider, service: Service) {
  return axios
    .get(`${API_URL}/providers/${provider.uuid}/services/?service=${service.uuid}`)
    .then((res) => {
      if (res.data.length === 1) return res.data[0];
      else if (res.data.length === 0) return undefined;
      else if (res.data.length > 1) throw new Error('Multiple provider services found');
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'getProviderService',
        data: { provider: provider.uuid, service: service.uuid },
      });
      throw error;
    });
}

function getProviderServiceByProviderAndParentProviderService(
  provider: Provider,
  parentProviderService: ProviderService
) {
  return axios
    .get(`${API_URL}/providers/${provider.uuid}/services/?parent_provider_service=${parentProviderService.uuid}`)
    .then((res) => {
      if (res.data.length === 1) return res.data[0];
      else if (res.data.length === 0) return undefined;
      else if (res.data.length > 1) throw new Error('Multiple provider services found');
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'getProviderServiceByProviderAndParentProviderService',
        data: { provider: provider.uuid, parentProviderService: parentProviderService.uuid },
      });
      throw error;
    });
}

function getProviderServiceInvite(uuid: uuid) {
  return axios
    .get(`${API_URL}/provider-services/${uuid}/invite/`)
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'getProviderServiceInvite',
        data: { uuid },
      });
      throw error;
    });
}

// Service Bundles (clinic-level)
function getServiceBundles(provider?: Provider): Promise<any[] | undefined> {
  if (!provider?.uuid) return Promise.resolve([]);
  return axios
    .get(`${API_URL}/providers/${provider.uuid}/service-bundles/`)
    .then((res) => res.data)
    .catch((error) => {
      monitoringCaptureError(error, { module: 'api', func: 'getServiceBundles', data: { provider: provider?.uuid } });
      throw error;
    });
}

function createServiceBundle(
  provider: Provider,
  bundle: { name: string; description?: string; items: { provider_service: string; qty: number }[] }
) {
  return axios
    .post(`${API_URL}/providers/${provider.uuid}/service-bundles/`, bundle)
    .then((res) => res.data)
    .catch((error) => {
      monitoringCaptureError(error, { module: 'api', func: 'createServiceBundle', data: { provider: provider?.uuid, bundle } });
      throw error;
    });
}

function updateServiceBundle(
  provider: Provider,
  bundleUuid: string,
  patch: Partial<{ name: string; description?: string; items: { provider_service: string; qty: number }[] }>
) {
  return axios
    .patch(`${API_URL}/providers/${provider.uuid}/service-bundles/${bundleUuid}/`, patch)
    .then((res) => res.data)
    .catch((error) => {
      monitoringCaptureError(error, { module: 'api', func: 'updateServiceBundle', data: { provider: provider?.uuid, bundleUuid, patch } });
      throw error;
    });
}

function getProviderServicesCount(provider?: Provider) {
  return axios
    .get(`${API_URL}/providers/${provider?.uuid}/services/count/`)
    .then((res) => {
      return res.data?.count;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'getProviderServiceCount',
        data: { provider: provider?.uuid },
      });
      throw error;
    });
}

function getProviderByInvite(invite?: string): Promise<IProviderByInvite> {
  return axios
    .get(`${API_URL}/providers/${invite}/public/`)
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      console.error('Failed to fetch invite info');
      throw error;
    });
}

function updateProviderService(providerService: ProviderService, update: ProviderService) {
  return axios.patch(`${API_URL}/provider-services/${providerService.uuid}/`, update);
}

function useUpdateProviderService(
  onSuccess?: (provider_service: ProviderService) => void,
  onError?: (error?: any) => void
) {
  const queryClient = useQueryClient();
  return useMutation(
    (variables: { providerService: ProviderService | undefined; update: ProviderService }) => {
      if (!variables.providerService) throw new Error('Provider service is required');
      return updateProviderService(variables.providerService, variables.update);
    },
    {
      onSuccess: (updatedItem) => {
        queryClient.invalidateQueries('provider-services');
        if (onSuccess) onSuccess(updatedItem.data);
      },
      onError: (error, variables) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useUpdateProviderService',
          data: { providerService: variables.providerService, update: variables.update },
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

function setupProviderServices(provider: Provider, config: any) {
  return axios
    .post(`${API_URL}/providers/${provider?.uuid}/services/setup/`, config)
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'setupProviderServices',
        data: { provider: provider?.uuid, config: config },
      });
      throw error;
    });
}

function useCreateProviderService(
  onSuccess?: (provider_service: ProviderService) => void,
  onError?: (error?: any) => void
) {
  const queryClient = useQueryClient();
  return useMutation(
    (variables: { service?: Service; providerService: ProviderService }) => {
      return axios.post(`${API_URL}/provider-services/`, variables.providerService);
    },
    {
      onSuccess: (updatedItem) => {
        queryClient.invalidateQueries('provider-services');
        if (onSuccess) onSuccess(updatedItem.data);
      },
      onError: (error, variables) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useCreateProviderService',
          data: { service: variables.service, providerService: variables.providerService },
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

function useProviderTimeSlots(onError?: (error?: any) => void) {
  return useQuery(
    'provider-timeslots',
    async (): Promise<Array<ProviderTimeSlot>> => {
      const { data } = await axios.get(`${API_URL}/provider-timeslots/`);
      return data;
    },
    {
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useProviderTimeSlots',
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

function useUpdateProviderTimeSlot(props: {
  onSuccess?: (providerTimeSlot: ProviderTimeSlot) => void;
  onError?: (error?: any) => void;
}) {
  const { onSuccess, onError } = props;
  const queryClient = useQueryClient();
  return useMutation(
    (variables: { providerTimeSlot: ProviderTimeSlot; update: ProviderTimeSlot; urlOptions?: any }) => {
      const { providerTimeSlot, update, urlOptions } = variables;
      const urlString = urlOptions ? queryString.stringify(urlOptions) : '';
      const newUpdate = {
        ...update,
        start_date: update.start_date ? moment(update.start_date).format('YYYY-MM-DD') : null,
        end_date: update.end_date ? moment(update.end_date).format('YYYY-MM-DD') : null,
      };
      return axios.patch(`${API_URL}/provider-timeslots/${providerTimeSlot?.uuid}/?${urlString}`, newUpdate);
    },
    {
      onSuccess: (updatedItem) => {
        queryClient.invalidateQueries('provider-timeslots');
        if (onSuccess) onSuccess(updatedItem.data);
      },
      onError: (error, variables) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useUpdateProviderTimeSlot',
          data: { providerTimeSlot: variables.providerTimeSlot, update: variables.update },
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

function useCreateProviderTimeSlot(props: {
  onSuccess?: (providerTimeSlot: ProviderTimeSlot) => void;
  onError?: (error?: any) => void;
}) {
  const { onSuccess, onError } = props;
  const queryClient = useQueryClient();
  return useMutation(
    (variables: { providerTimeSlot: ProviderTimeSlot; urlOptions?: any }) => {
      const { providerTimeSlot, urlOptions } = variables;
      const urlString = urlOptions ? queryString.stringify(urlOptions) : '';
      const newProviderTimeSlot = {
        ...providerTimeSlot,
        start_date: providerTimeSlot.start_date ? moment(providerTimeSlot.start_date).format('YYYY-MM-DD') : null,
        end_date: providerTimeSlot.end_date ? moment(providerTimeSlot.end_date).format('YYYY-MM-DD') : null,
      };
      return axios.post(`${API_URL}/provider-timeslots/?${urlString}`, newProviderTimeSlot);
    },
    {
      onSuccess: (updatedItem) => {
        queryClient.invalidateQueries('provider-timeslots');
        if (onSuccess) onSuccess(updatedItem.data);
      },
      onError: (error, variables) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useCreateProviderTimeSlot',
          data: { providerTimeSlot: variables.providerTimeSlot, urlOptions: variables.urlOptions },
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

function useProviderTimeSlotServices(onError?: (error?: any) => void) {
  return useQuery(
    'provider-timeslot-services',
    async (): Promise<Array<ProviderTimeSlotService>> => {
      const { data } = await axios.get(`${API_URL}/provider-timeslot-services/`);
      return data;
    },
    {
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useProviderTimeSlotServices',
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

function setupProviderTimeSlot(provider: Provider, config: any) {
  return axios
    .post(`${API_URL}/providers/${provider?.uuid}/timeslots/setup/`, config)
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'setupProviderTimeSlot',
        data: { provider: provider?.uuid, config: config },
      });
      throw error;
    });
}

function getProviderTimeSlotCount(provider?: Provider) {
  return axios
    .get(`${API_URL}/providers/${provider?.uuid}/timeslots/count/`)
    .then((res) => {
      return res.data?.count;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'getProviderTimeSlotCount',
        data: { provider: provider?.uuid },
      });
      throw error;
    });
}

function useProviderMe(onError?: (error?: any) => void) {
  return useQuery(
    ['providers', 'me'],
    async (): Promise<Provider> => {
      const { data } = await axios.get(`${API_URL}/providers/me/`);
      return data;
    },
    {
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useProviderMe',
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

function useProvider(id?: uuid, onError?: (error?: any) => void) {
  return useQuery(
    ['providers', id],
    async (): Promise<Provider> => {
      const { data } = await axios.get(`${API_URL}/providers/${id}/`);
      return data;
    },
    {
      enabled: !!id,
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useProvider',
          id: id,
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

function useClinic(clinic_uuid: uuid | undefined, onError?: (error?: any) => void) {
  return useQuery(
    ['clinic'],
    async (): Promise<Clinic> => {
      const { data } = await axios.get(`${API_URL}/clinics/${clinic_uuid}/`);
      return data;
    },
    {
      enabled: clinic_uuid !== undefined,
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useClinic',
          data: { clinic_uuid },
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

function useUpdateProvider(
  provider: Provider,
  update: Provider,
  onSuccess?: (provider: Provider) => void,
  onError?: (error?: any) => void
) {
  const queryClient = useQueryClient();
  return useMutation(
    () => {
      return axios.patch(`${API_URL}/providers/${provider?.uuid}/`, update);
    },
    {
      onSuccess: (updatedItem) => {
        queryClient.invalidateQueries('providers');
        if (onSuccess) onSuccess(updatedItem.data);
      },
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useUpdateProvider',
          data: { provider, update },
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

function useClients(props?: { enabled?: boolean; options?: any; onError?: (error?: any) => void }) {
  const { currentUser } = useAuth();
  const query = props?.options ? qs.stringify(props.options, { skipNulls: true }) : '';
  return useQuery(
    'clients',
    async (): Promise<Array<Client>> => {
      const { data } = await axios.get(`${API_URL}/clients/?${query}`);
      return data;
    },
    {
      enabled: props?.enabled !== false && currentUser !== undefined,
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useClients',
          data: { enabled: props?.enabled },
        });
        if (props?.onError) props?.onError(error);
        else throw error;
      },
    }
  );
}

function getClientsCount() {
  return axios
    .get(`${API_URL}/clients/count/`)
    .then((res) => {
      return res.data?.count;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'getClientsCount',
      });
      throw error;
    });
}

function useGuardians(onError?: (error?: any) => void) {
  return useQuery(
    'guardians',
    async (): Promise<Array<Guardian>> => {
      const { data } = await axios.get(`${API_URL}/guardians/`);
      return data;
    },
    {
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useGuardians',
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

function useProviders(onError?: (error?: any) => void) {
  return useQuery(
    ['providers'],
    async (): Promise<Array<Provider>> => {
      const { data } = await axios.get(`${API_URL}/providers/`);
      return data;
    },
    {
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useProviders',
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

function getProviderPaymentEnabled(provider: Provider) {
  return axios
    .get(`${API_URL}/providers/${provider.uuid}/payment_enabled/`)
    .then((res) => {
      return res.data?.payment_enabled;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'getProviderPaymentEnabled',
      });
      throw error;
    });
}

function useAges(onError?: (error?: any) => void) {
  return useQuery(
    'ages',
    async (): Promise<Array<AgeGroup>> => {
      const { data } = await axios.get(`${API_URL}/ages/`);
      return data;
    },
    {
      cacheTime: 24 * 60 * 60 * 1000,
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useAges',
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

function useExpertises(onError?: (error?: any) => void) {
  return useQuery(
    'expertises',
    async (): Promise<Array<Expertise>> => {
      const { data } = await axios.get(`${API_URL}/expertises/`);
      return data;
    },
    {
      cacheTime: 24 * 60 * 60 * 1000,
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useExpertises',
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

function useConcerns(onError?: (error?: any) => void) {
  return useQuery(
    'concerns',
    async (): Promise<Array<Concern>> => {
      const { data } = await axios.get(`${API_URL}/concerns/`);
      return data;
    },
    {
      cacheTime: 24 * 60 * 60 * 1000,
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useConcerns',
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

function useLanguages(onError?: (error?: any) => void) {
  return useQuery(
    'languages',
    async (): Promise<Array<Language>> => {
      const { data } = await axios.get(`${API_URL}/languages/`);
      return data;
    },
    {
      cacheTime: 24 * 60 * 60 * 1000,
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useLanguages',
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

function usePostalCodes(query: string | undefined, from?: string, onError?: (error?: any) => void) {
  return useQuery(
    ['postal-codes', query],
    async (): Promise<Array<PostalCode>> => {
      const { data } = await axios.get(`${API_URL}/postalcodes/?search=${query}${from ? `&from=${from}` : ''}`);
      return data;
    },
    {
      cacheTime: 24 * 60 * 60 * 1000,
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'usePostalCodes',
        });
        if (onError) onError(error);
        else throw error;
      },
      enabled: !!query && query.trim().length > 0,
    }
  );
}

const getPostalCode = async (
  postalCodeString: string,
  onError?: (error?: any) => void
): Promise<PostalCode | undefined> => {
  const res = await axios.get(`${API_URL}/postalcodes/?search=${postalCodeString}`);
  if (res?.data && res?.data.length > 0) return res?.data[0];
  return undefined;
};

const getZipTimezone = async (zip: string): Promise<string | null> => {
  try {
    const res = await axios.get(`${API_URL}/postalcodes/timezone/?zip=${encodeURIComponent(zip)}`);
    return res?.data?.timezone ?? null;
  } catch (error) {
    monitoringCaptureError(error, { module: 'api', func: 'getZipTimezone' });
    return null;
  }
};

export type PlaceTimezoneMatch = { place_name: string; admin_code1: string; timezone: string };

const getPlaceTimezones = async (q: string): Promise<PlaceTimezoneMatch[]> => {
  try {
    const res = await axios.get(`${API_URL}/postalcodes/timezone/place/?q=${encodeURIComponent(q)}`);
    return res?.data?.matches ?? [];
  } catch (error) {
    monitoringCaptureError(error, { module: 'api', func: 'getPlaceTimezones' });
    return [];
  }
};

function useTimeSlots(onError?: (error?: any) => void) {
  return useQuery(
    'timeslots',
    async (): Promise<Array<TimeSlot>> => {
      const { data } = await axios.get(`${API_URL}/timeslots/`);
      return data;
    },
    {
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useTimeSlots',
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

function useUpdateTimeSlot(
  timeSlot: TimeSlot,
  update: TimeSlot,
  onSuccess?: (timeSlot: TimeSlot) => void,
  onError?: (error?: any) => void
) {
  const queryClient = useQueryClient();
  return useMutation(
    () => {
      return axios.patch(`${API_URL}/timeslots/${timeSlot?.uuid}/`, update);
    },
    {
      onSuccess: (updatedItem) => {
        queryClient.invalidateQueries('timeslots');
        if (onSuccess) onSuccess(updatedItem.data);
      },
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useUpdateTimeSlot',
          data: { timeSlot, update },
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

function useCreateTimeSlot(
  timeSlot: TimeSlot,
  onSuccess?: (timeSlot: TimeSlot) => void,
  onError?: (error?: any) => void
) {
  const queryClient = useQueryClient();
  return useMutation(
    () => {
      return axios.post(`${API_URL}/timeslots/`, timeSlot);
    },
    {
      onSuccess: (updatedItem) => {
        queryClient.invalidateQueries('timeslots');
        if (onSuccess) onSuccess(updatedItem.data);
      },
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useCreateTimeSlot',
          data: { timeSlot },
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

function getEvents(props: {
  start?: Date | undefined | null;
  end?: Date | undefined | null;
  client?: Client;
  singleEvents?: boolean;
  source?: string[];
  providerUuids?: ID[];
  provider?: Provider;
  providerService?: ProviderService;
  onError?: (error?: any) => void;
}) {
  const { start, end, client, providerUuids, singleEvents, source, provider, providerService, onError } = props;
  const params = new URLSearchParams();

  start && params.append('start_date', start?.toISOString());
  end && params.append('end_date', end?.toISOString());
  client?.uuid && params.append('client', client.uuid);
  providerUuids && params.append('providers', providerUuids.join(','));
  singleEvents && params.append('single_events', 'true');
  source && params.append('source', source.join(','));
  providerService?.uuid && params.append('provider_service', providerService.uuid);

  if (provider?.uuid && !providerUuids) {
    params.append('providers', provider.uuid.toString());
  }

  return axios
    .get(`${API_URL}/events/?${params.toString()}`)
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'getEvents',
        data: { start, end, client: client && client.uuid, singleEvents },
      });
      throw error;
    });
}

function useEvents(props: {
  start?: Date | null | undefined;
  end?: Date | null | undefined;
  client?: Client;
  provider?: Provider;
  singleEvents?: boolean;
  source?: string[];
  providerUuids?: ID[];
  providerService?: ProviderService;
  enabled?: boolean;
  onError?: (error?: any) => void;
}) {
  const { currentProvider } = useAuth();
  const { start, end, client, provider, singleEvents, source, providerUuids, providerService, enabled, onError } = props;
  return useQuery(
    [
      'events',
      { start, end },
      client?.uuid,
      currentProvider?.uuid,
      providerUuids,
      source,
      singleEvents,
      provider?.uuid,
      providerService?.uuid,
    ],
    async (): Promise<Array<Event>> => {
      return await getEvents(props);
    },
    {
      enabled: !!start && !!end && enabled !== false,
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 10,
      refetchOnWindowFocus: false,
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useEvents',
          data: { start, end },
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

function getEventsForSchedule(props: {
  start?: Date | undefined | null;
  end?: Date | undefined | null;
  client?: Client;
  source?: string[];
  providerUuids?: ID[];
  provider?: Provider;
  providerService?: ProviderService;
  onError?: (error?: any) => void;
}) {
  const { start, end, client, providerUuids, source, provider, providerService, onError } = props;
  const params = new URLSearchParams();

  start && params.append('start_date', start?.toISOString());
  end && params.append('end_date', end?.toISOString());
  client?.uuid && params.append('client', client.uuid);
  providerUuids && params.append('providers', providerUuids.join(','));
  source && params.append('source', source.join(','));
  providerService?.uuid && params.append('provider_service', providerService.uuid);

  if (provider?.uuid && !providerUuids) {
    params.append('providers', provider.uuid.toString());
  }

  return axios
    .get(`${API_URL}/events/schedule/?${params.toString()}`)
    .then((res) => res.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'getEventsForSchedule',
        data: { start, end, client: client && client.uuid },
      });
      throw error;
    });
}

function useEventsForSchedule(props: {
  start?: Date | null | undefined;
  end?: Date | null | undefined;
  client?: Client;
  provider?: Provider;
  source?: string[];
  providerUuids?: ID[];
  providerService?: ProviderService;
  enabled?: boolean;
  onError?: (error?: any) => void;
}) {
  const { currentProvider } = useAuth();
  const { start, end, client, provider, source, providerUuids, providerService, enabled, onError } = props;
  return useQuery(
    [
      'events-schedule',
      { start, end },
      client?.uuid,
      currentProvider?.uuid,
      providerUuids,
      source,
      provider?.uuid,
      providerService?.uuid,
    ],
    async (): Promise<Array<Event>> => {
      return await getEventsForSchedule(props);
    },
    {
      enabled: !!start && !!end && enabled !== false,
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 10,
      refetchOnWindowFocus: false,
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useEventsForSchedule',
          data: { start, end },
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

function updateEvent(props: {
  event: Event;
  update: Event;
  original_date?: Date;
  date?: Date;
  following?: Date;
  all?: Date;
}) {
  let url = `${API_URL}/events/${props.event?.uuid}/?`;
  if (props.original_date) url += `original_date=${props.original_date.toISOString()}`;
  if (props.date) url += `&date=${props.date.toISOString()}`;
  else if (props.following) url += `&following=${props.following?.toISOString()}`;
  else if (props.all) url += `&all=${props.all?.toISOString()}`;
  return axios
    .patch(url, props.update)
    .then((res) => res.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'updateEvent',
        data: {
          event: props.event.uuid,
          update: props.update,
          date: props.date,
          following: props.following,
          all: props.all,
        },
      });
      throw error;
    });
}

function updateEventSubscription(props: { event: Event; subscription: ClientSubscription }) {
  let url = `${API_URL}/events/${props.event?.uuid}/update-subscription/?`;
  return axios
    .patch(url, props.subscription)
    .then((res) => res.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'updateEventSubscription',
        data: {
          event: props.event.uuid,
          subscription: props.subscription,
        },
      });
      throw error;
    });
}

// Attach a one-off Bill to a (non-recurring) event. Thin wrapper over the
// generic events PATCH — the backend validates that the bill belongs to the
// event's client and rejects recurring events (scheduling/serializers.py).
function updateEventOneoff(props: { event: Event; oneoff: ClientOneOffPayment }) {
  if (!props.oneoff.uuid) return Promise.reject(new Error('updateEventOneoff: oneoff has no uuid'));
  return updateEvent({ event: props.event, update: { oneoff: props.oneoff.uuid } });
}

function useUpdateEvent(props: {
  event: Event;
  original_date?: Date;
  date?: Date;
  following?: Date;
  all?: Date;
  onSuccess?: (event: Event) => void;
  onError?: (error?: any) => void;
}) {
  const queryClient = useQueryClient();
  return useMutation(
    (update: Event) => {
      return updateEvent({ ...props, update });
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('events');
        if (props.onSuccess) props.onSuccess(data);
      },
      onError: (error, variables) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useUpdateEvent',
          data: { event, variables },
        });
        if (props.onError) props.onError(error);
        else throw error;
      },
    }
  );
}

function getRescheduleEvent(token?: string): Promise<EventLog> {
  return axios
    .get(`${API_URL}/reschedule/${token}/`)
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'getRescheduleEvent',
        data: { token },
      });
      throw error;
    });
}

function rescheduleEvent(props: { token?: string; eventLog?: EventLog }): Promise<EventLog> {
  const { token, eventLog } = props;
  return axios
    .patch(`${API_URL}/reschedule/${token}/`, eventLog)
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'updateEventLog',
        data: { token, eventLog },
      });
      throw error;
    });
}

function useCreateEvent(onSuccess?: (event: Event) => void, onError?: (error?: any) => void) {
  const queryClient = useQueryClient();
  return useMutation(
    (event: Event) => {
      return axios.post(`${API_URL}/events/`, event);
    },
    {
      onSuccess: (updatedItem) => {
        queryClient.invalidateQueries('events');
        if (onSuccess) onSuccess(updatedItem.data);
      },
      onError: (error, variables) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useCreateEvent',
          data: { variables },
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

function importEvent(
  event: Event,
  client: Client,
  provider: Provider,
  providerService?: ProviderService,
  subscription?: ClientSubscription
) {
  return axios
    .post(`${API_URL}/events/${event?.uuid}/import/`, {
      client,
      provider,
      provider_service: providerService,
      subscription,
    })
    .then((res) => res.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'importEvent',
        data: { event, client: client.uuid, provider, providerService, subscription },
      });
      throw error;
    });
}

function getEventPaymentStatus(props: { event?: Event; dt?: Date }) {
  return axios
    .get(`${API_URL}/events/${props.event?.uuid}/payment-status/?dt=${props.dt?.toISOString()}&include-inactive=true`)
    .then((res) => {
      if (res.data && res.data.status) return res.data.status;
      else return undefined;
    })
    .catch((err) => {
      monitoringCaptureError(err, {
        module: 'api',
        func: 'getEventPaymentStatus',
        data: { props },
      });
      return Promise.resolve(undefined);
    });
}

function getRelatedFutureEvents(eventUuid: string, fromDate: Date): Promise<any[]> {
  return axios
    .get(`${API_URL}/events/${eventUuid}/related-future-events/?from_date=${fromDate.toISOString()}`)
    .then((res) => res.data?.related_events || [])
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'getRelatedFutureEvents',
        data: { eventUuid, fromDate },
      });
      return [];
    });
}

function getBatchEventPaymentStatus(eventDates: { event: Event; dt: Date }[]): Promise<Record<string, string>> {
  if (eventDates.length === 0) {
    return Promise.resolve({});
  }

  const event_dates = eventDates.map(({ event, dt }) => ({
    event_uuid: event.uuid,
    dt: dt.toISOString(),
  }));

  return axios
    .post(`${API_URL}/events/batch-payment-status/`, { event_dates })
    .then((res) => {
      return res.data?.results || {};
    })
    .catch((err) => {
      monitoringCaptureError(err, {
        module: 'api',
        func: 'getBatchEventPaymentStatus',
        data: { eventDates },
      });
      return Promise.resolve({});
    });
}

function useDeleteEvent(props: { onSuccess?: (timeSlot: Event) => void; onError?: (error?: any) => void }) {
  const queryClient = useQueryClient();
  return useMutation(
    (variables: { event: Event; date?: Date; following?: Date; all?: Date }) => {
      let url = `${API_URL}/events/${variables.event?.uuid}/?`;
      if (variables.date) url += `date=${variables.date.toISOString()}`;
      else if (variables.following) url += `following=${variables.following?.toISOString()}`;
      else if (variables.all) url += `all=${variables.all?.toISOString()}`;

      return axios.delete(url);
    },
    {
      onSuccess: (updatedItem) => {
        queryClient.invalidateQueries('events');
        if (props.onSuccess) props.onSuccess(updatedItem.data);
      },
      onError: (error, variables) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useUpdateEvent',
          data: { event: variables.event.uuid, date: variables.date, following: variables.following },
        });
        if (props.onError) props.onError(error);
        else throw error;
      },
    }
  );
}

function useEventsLog(props: {
  start?: Date | null;
  end?: Date | null;
  client?: Client;
  provider?: Provider;
  providerUuids?: ID[];
  statuses?: string[];
  enabled?: boolean;
  onError?: (error?: any) => void;
}) {
  const { currentProvider } = useAuth();
  const { start, end, client, provider, providerUuids, statuses, enabled, onError } = props;
  const params = new URLSearchParams();

  start && params.append('start', start?.toISOString());
  end && params.append('end', end?.toISOString());
  client?.uuid && params.append('client', client.uuid);
  providerUuids && params.append('providers', providerUuids.join(','));
  statuses && params.append('statuses', statuses.join(','));

  if (provider?.uuid && !providerUuids) {
    params.append('providers', provider.uuid.toString());
  }

  return useQuery(
    ['events-log', { start, end }, client?.uuid, providerUuids, statuses, provider?.uuid, currentProvider?.uuid],
    async (): Promise<Array<EventLog>> => {
      const { data } = await axios.get(`${API_URL}/events-log/?${params.toString()}`);

      //   `${API_URL}/events-log/?start=${start?.toISOString()}&end=${end?.toISOString()}
      //    ${client ? `&client=${client.uuid}` : ''}
      //    ${providerUuids ? `&providers=${providerUuids.join(',')}` : ''}
      //    ${statuses ? `&statuses=${statuses.join(',')}` : ''}
      //    `
      // );
      return data;
    },
    {
      enabled: !!start && !!end && enabled !== false,
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 10,
      refetchOnWindowFocus: false,
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useEventsLog',
          data: { start, end },
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

function useSessionsReport(props: {
  start?: string | null;
  end?: string | null;
  client?: Client;
  provider?: Provider;
  providerUuids?: ID[];
  statuses?: string[];
  source?: string[];
  tagUuids?: string[];
  enabled?: boolean;
  onError?: (error?: any) => void;
}) {
  const { currentProvider } = useAuth();
  const { start, end, client, provider, providerUuids, statuses, source, tagUuids, enabled, onError } = props;
  const params = new URLSearchParams();

  start && params.append('start', start);
  end && params.append('end', end);
  client?.uuid && params.append('client', client.uuid);
  providerUuids && params.append('providers', providerUuids.join(','));
  statuses && params.append('statuses', statuses.join(','));
  source && params.append('source', source.join(','));
  tagUuids?.length && params.append('filter_tags', tagUuids.join(','));

  if (provider?.uuid && !providerUuids) {
    params.append('providers', provider.uuid.toString());
  }

  return useQuery(
    ['sessions-report', { start, end }, client?.uuid, providerUuids, statuses, source, tagUuids, provider?.uuid, currentProvider?.uuid],
    async (): Promise<Array<any>> => {
      const { data } = await axios.get(`${API_URL}/events/sessions-report/?${params.toString()}`);
      return data;
    },
    {
      enabled: !!start && !!end && enabled !== false,
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 10,
      refetchOnWindowFocus: false,
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useSessionsReport',
          data: { start, end },
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

function getEvent(uuid: uuid, includeInactive: boolean = false) {
  const params = includeInactive ? '?include-inactive=true' : '';
  return axios.get(`${API_URL}/events/${uuid}/${params}`).then((res) => res.data);
}

function getEventLog(props: { event?: Event; start?: Date; uuid?: uuid }): Promise<EventLog | undefined> {
  if (props.uuid) return axios.get(`${API_URL}/events-log/${props.uuid}/`).then((res) => res.data);
  else if (props.event?.uuid)
    return axios
      .get(`${API_URL}/events-log/?event=${props.event?.uuid}&start=${props.start?.toISOString()}`)
      .then((res) => {
        if (res.data && Array.isArray(res.data) && res.data.length > 0) return res.data[0];
        else return undefined;
      });
  else {
    monitoringCaptureError(new Error('getEventLog called with undefined uuid and event.uuid'), {
      module: 'api',
      func: 'getEventLog',
      data: { props },
    });
    return Promise.resolve(undefined);
  }
}

function getEventLogFromEvent(props: { event_uuid: uuid; datetime: Date }): Promise<EventLog> {
  return axios
    .post(`${API_URL}/events-log/from_event/`, {
      event_uuid: props.event_uuid,
      datetime: props.datetime.toISOString(),
    })
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'getEventLogFromEvent',
        data: { props },
      });
      throw error;
    });
}

function updateEventLog(eventLog: EventLog, update?: EventLog): Promise<EventLog> {
  return axios
    .patch(`${API_URL}/events-log/${eventLog?.uuid}/`, update)
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'updateEventLog',
        data: { eventLog, update },
      });
      throw error;
    });
}

function createEventLog(eventLog: EventLog): Promise<EventLog> {
  return axios
    .post(`${API_URL}/events-log/`, eventLog)
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'createEventLog',
        data: { eventLog },
      });
      throw error;
    });
}

function getClientProviderId(client?: Client, provider?: Provider) {
  if (client && provider)
    return axios
      .get(`${CLIENT_PROVIDER_URL}get-id/?client=${client.uuid}&provider=${provider.uuid}`)
      .then((res) => res.data)
      .catch((error) => {
        return undefined;
      });
  else return Promise.resolve(undefined);
}

function getClientProviderById(client?: Client, provider?: Provider): Promise<ClientProvider | undefined> {
  if (client && provider)
    return axios
      .get(`${CLIENT_PROVIDER_URL}get-by-id/?client=${client.uuid}&provider=${provider.uuid}`)
      .then((res) => res.data)
      .catch((error) => {
        return undefined;
      });
  else return Promise.resolve(undefined);
}

function getClientProvidersCount(query?: string) {
  return axios
    .get(`${API_URL}/client-providers/count/?${query}`)
    .then((res) => {
      return res.data?.count;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'getClientProvidersCount',
        data: { query },
      });
      throw error;
    });
}

function downloadClientProvidersCSV(query?: string): Promise<boolean> {
  const url = `${API_URL}/client-providers/csv/?${query}`;
  return axios
    .get(url, {
      responseType: 'blob',
    })
    .then((res) => {
      const contentDisposition = res.headers['content-disposition'];
      let filename = 'clients.csv';
      if (contentDisposition) {
        const matches = /filename="(.+)"/.exec(contentDisposition);
        if (matches && matches[1]) {
          filename = matches[1];
        }
      }
      fileDownload(res.data, filename);
      return true;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'downloadClientProvidersCSV',
        data: { query },
      });
      throw error;
    });
}

function useClientProviders(enabled?: boolean, onError?: (error?: any) => void, excludeContacts?: boolean) {
  const { currentUser } = useAuth();
  return useQuery(
    ['client-providers', excludeContacts],
    async (): Promise<Array<ClientProvider>> => {
      const params = excludeContacts ? { exclude_contacts: 'true' } : {};
      const { data } = await axios.get(`${CLIENT_PROVIDER_URL}`, { params });
      return data;
    },
    {
      enabled: enabled !== false && currentUser !== undefined,
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useClientProviders',
          data: { enabled, excludeContacts },
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

function getClientProvider(id?: ID, onError?: (error?: any) => void): Promise<ClientProvider> {
  return axios
    .get(`${API_URL}/client-providers/${id}/`)
    .then((res) => {
      const data = res.data;
      if (!data || data.error) {
        const error = new Error(data?.error || 'Client provider not found');
        if (onError) onError(error);
        else throw error;
      }
      return data;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'getClientProvider',
        data: { id },
      });
      if (onError) onError(error);
      else throw error;
    });
}

function useClientProvider(id?: ID, onError?: (error?: any) => void) {
  return useQuery(['client-providers', id], () => getClientProvider(id, onError), {
    enabled: !!id,
    retry: 0, // do not retry
    refetchOnWindowFocus: false,
    onError: (error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'useClientProvider',
        data: { id },
      });
      if (onError) onError(error);
      else throw error;
    },
  });
}

function useUpdateClientProvider(
  clientProvider: ClientProvider | undefined,
  update: ClientProvider,
  onSuccess?: (client_provider: ClientProvider) => void,
  onError?: (error?: any) => void
) {
  const queryClient = useQueryClient();
  return useMutation(
    () => {
      return axios.patch(`${API_URL}/client-providers/${clientProvider?.uuid}/`, update);
    },
    {
      onSuccess: (updatedItem) => {
        queryClient.invalidateQueries('client-providers');
        queryClient.invalidateQueries('clients');
        if (onSuccess) onSuccess(updatedItem.data);
      },
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useUpdateClientProvider',
          data: { clientProvider, update },
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

// ── Client Tags ─────────────────────────────────────────────────────

function useClientTags(enabled?: boolean) {
  return useQuery(
    ['client-tags'],
    async () => {
      const { data } = await axios.get(`${API_URL}/client-tags/`);
      return data?.data || data || [];
    },
    { enabled: enabled !== false }
  );
}

function createClientTag(tag: { name: string; color: string }) {
  return axios.post(`${API_URL}/client-tags/`, tag).then((res) => res.data);
}

function updateClientTag(uuid: string, tag: { name?: string; color?: string }) {
  return axios.patch(`${API_URL}/client-tags/${uuid}/`, tag).then((res) => res.data);
}

function deleteClientTag(uuid: string) {
  return axios.delete(`${API_URL}/client-tags/${uuid}/`);
}

// ── End Client Tags ─────────────────────────────────────────────────

const getOnboarding = (inviteId: string | undefined, onError?: (error?: any) => void) =>
  axios
    .get(`${API_URL}/onboarding/${inviteId}/`)
    .then((res) => res.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'getOnboarding',
      });
      if (onError) onError(error);
      else throw error;
    });

// WST-654 — token-authenticated payment-failure resolution flow
// WST-757 — `payment_status`, `payment_method_type`, and `action_allowed`
// tell the page whether to show Retry / Update Payment Method or a
// state-appropriate message (already paid, ACH-processing, etc.).
type PaymentResolutionData = {
  token: string;
  recipient_first_name: string;
  complete: boolean;
  resolved: boolean;
  invoice: {
    uuid: string;
    amount: number | null;
    currency: string;
    failed_at: string | null;
    failure_reason: string;
    payment_status: string;
    payment_method_type: string;
    action_allowed: boolean;
  };
  clinic: {
    uuid: string | null;
    name: string;
    is_org: boolean;
    logo_url: string | null;
    email: string | null;
    phone: string | null;
    website: string | null;
    domain: string;
  } | null;
  pk: string | null;
  ck: string | null;
};

const getPaymentResolution = (token: string | undefined): Promise<PaymentResolutionData> =>
  axios.get(`${API_URL}/payment-resolution/${token}/`).then((res) => res.data);

const retryPaymentResolution = (token: string | undefined): Promise<{ status?: string; error?: string; resolved?: boolean }> =>
  axios.post(`${API_URL}/payment-resolution/${token}/retry/`).then((res) => res.data);

const confirmMethodPaymentResolution = (
  token: string | undefined,
  payment_method_id: string,
  set_as_default: boolean,
): Promise<{ status?: string; error?: string; resolved?: boolean; set_as_default?: boolean }> =>
  axios
    .post(`${API_URL}/payment-resolution/${token}/confirm-method/`, {
      payment_method_id,
      set_as_default,
    })
    .then((res) => res.data);

function useOnboarding(inviteId: string | undefined, onError?: (error?: any) => void) {
  return useQuery(
    'onboarding',
    async (): Promise<ClientOnboarding> => {
      const { data } = await axios.get(`${API_URL}/onboarding/${inviteId}/`);
      return data;
    },
    {
      enabled: inviteId !== undefined,
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useOnboarding',
          data: { inviteId },
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

function updateOnboarding(
  inviteId: string | undefined,
  update: any,
  onSuccess?: (onboarding: ClientOnboarding) => void,
  onError?: (err: any) => void
) {
  return axios
    .patch(`${API_URL}/onboarding/${inviteId}/`, update)
    .then((d: AxiosResponse<ClientOnboarding>) => {
      if (onSuccess) onSuccess(d.data);
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'updateOnboarding',
        data: { inviteId, update },
      });
      if (onError) onError(error);
      else throw error;
    });
}

function useAvailableTimeslot(props: {
  serviceUuid: uuid;
  providerServiceUuid?: uuid;
  start: Date;
  end: Date;
  priceMax?: number;
  startTime?: string;
  timezone?: string;
  providersUuid?: uuid[];
  recurringOnly?: boolean;
  clientUuid?: uuid;
  anyProvider?: boolean;
  onError?: (error?: any) => void;
  [key: string]: any;
}) {
  const {
    serviceUuid,
    providerServiceUuid,
    start,
    end,
    priceMax,
    startTime,
    timezone,
    providersUuid,
    recurringOnly,
    clientUuid,
    anyProvider,
    onError,
    ...otherParams
  } = props;
  return useQuery(
    [
      'timeslot-availability',
      serviceUuid,
      clientUuid,
      start,
      end,
      priceMax,
      startTime,
      timezone,
      providersUuid,
      recurringOnly,
      anyProvider,
      otherParams,
    ],
    async (): Promise<AvailabilityList> => {
      // let url = `${SERVICE_TIMESLOT_URL(serviceUuid)}available/?start=${start.toISOString()}&end=${end.toISOString()}`;
      // if (providersUuid) url += `&providers=${providersUuid.join(',')}`;
      // if (priceMax) url += `&price__lte=${priceMax}`;
      // if (startTime) url += `&start_time__gte=${startTime}`;
      // if (timezone) url += `&tz=${timezone}`;
      // if (recurringOnly) url += `&recurring=${recurringOnly}`;
      // if (clientUuid) url += `&client=${clientUuid}`;
      // return await axios.get(url).then((d: AxiosResponse<AvailabilityList>) => d.data);

      let params = {
        start: start,
        end: end,
        providers: providersUuid,
        price__lte: priceMax,
        start_time__gte: startTime,
        tz: timezone,
        recurring: recurringOnly,
        client: clientUuid,
        any_provider: anyProvider,
        utz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...otherParams,
      };
      const url = serviceUuid ? SERVICE_TIMESLOT_URL(serviceUuid) : PROVIDER_SERVICE_TIMESLOT_URL(providerServiceUuid);
      return await axios
        .post(`${url}available/`, params)
        .then((d: AxiosResponse<AvailabilityList>) => d.data);
    },
    {
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useAvailableTimeslot',
          data: { serviceUuid, start, end, priceMax, startTime, timezone, providersUuid, recurringOnly, clientUuid },
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

function availableTimeslotCount(props: { serviceUuid: uuid; providerServiceUuid?: uuid; providersUuid?: uuid[]; start: Date; end: Date }) {
  const { serviceUuid, providerServiceUuid, providersUuid, start, end } = props;
  const url = serviceUuid ? SERVICE_TIMESLOT_URL(serviceUuid) : PROVIDER_SERVICE_TIMESLOT_URL(providerServiceUuid);
  return axios
    .get(`${url}available/count/`, {
      params: { providers: providersUuid, start: start, end: end },
    })
    .then((res) => {
      return res.data?.count;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'availableTimeslotCount',
        data: { serviceUuid, providersUuid, start, end },
      });
      throw error;
    });
}

const holdAvailableTimeslot = (
  serviceUuid: uuid,
  providerServiceUuid?: uuid,
  timeslotUuid?: uuid,
  clientUuid?: uuid,
  onError?: (error?: any) => void
) => {
  const url = serviceUuid ? SERVICE_TIMESLOT_URL(serviceUuid) : PROVIDER_SERVICE_TIMESLOT_URL(providerServiceUuid);
  return axios.patch(`${url}${timeslotUuid}/hold/?client=${clientUuid}`).catch((error) => {
    monitoringCaptureError(error, {
      module: 'api',
      func: 'holdAvailableTimeslot',
      data: { serviceUuid, timeslotUuid, clientUuid },
    });
    if (onError) onError(error);
    else throw error;
  });
}

function useAvailableTimeslotHold(
  serviceUuid: uuid,
  providerServiceUuid?: uuid,
  timeslotUuid?: uuid,
  clientUuid?: uuid,
  onSuccess?: () => void,
  onError?: (error?: any) => void
) {
  const queryClient = useQueryClient();
  return useMutation(
    () => {
      return holdAvailableTimeslot(serviceUuid, providerServiceUuid, timeslotUuid, clientUuid);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('timeslot-availability');
        if (onSuccess) onSuccess();
      },
      onError: (error) => {
        queryClient.invalidateQueries('timeslot-availability');
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useUpdateEvent',
          data: { serviceUuid, timeslotUuid, clientUuid },
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

const releaseAvailableTimeslot = (
  serviceUuid: uuid,
  providerServiceUuid?: uuid,
  timeslotUuid?: uuid,
  clientUuid?: uuid,
  onError?: (error?: any) => void
) =>{
  const url = serviceUuid ? SERVICE_TIMESLOT_URL(serviceUuid) : PROVIDER_SERVICE_TIMESLOT_URL(providerServiceUuid);
  return axios.patch(`${url}${timeslotUuid}/release/?client=${clientUuid}`).catch((error) => {
    monitoringCaptureError(error, {
      module: 'api',
      func: 'releaseAvailableTimeslot',
      data: { serviceUuid, timeslotUuid, clientUuid },
    });
    if (onError) onError(error);
    else throw error;
  });
}
function useAvailableTimeslotRelease(
  serviceUuid: uuid,
  providerServiceUuid?: uuid,
  timeslotUuid?: uuid,
  clientUuid?: uuid,
  onSuccess?: () => void,
  onError?: (error?: any) => void
) {
  const queryClient = useQueryClient();
  return useMutation(
    () => {
      return releaseAvailableTimeslot(serviceUuid, timeslotUuid, clientUuid);
    },
    {
      onSuccess: (updatedItem) => {
        queryClient.invalidateQueries('timeslot-availability');
        if (onSuccess) onSuccess();
      },
      onError: (error) => {
        queryClient.invalidateQueries('timeslot-availability');
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useAvailableTimeslotRelease',
          data: { serviceUuid, timeslotUuid, clientUuid },
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

const bookAvailableTimeslots = (
  serviceUuid: uuid,
  providerServiceUuid?: uuid,
  timeslotUuids?: Array<uuid>,
  clientUuid?: uuid,
  oneOffUuid?: uuid,
  subscriptionUuid?: uuid,
  guestEmails?: string[],
  onError?: (error?: any) => void,
  session?: { hybrid_type?: string; phone_number?: string }
) => {
  const url = serviceUuid ? SERVICE_TIMESLOT_URL(serviceUuid) : PROVIDER_SERVICE_TIMESLOT_URL(providerServiceUuid);
  return axios
    .post(`${url}book/`, {
      client: clientUuid,
      slots: timeslotUuids,
      oneoff: oneOffUuid,
      subscription: subscriptionUuid,
      guest_emails: guestEmails && guestEmails.length ? guestEmails : undefined,
      hybrid_type: session?.hybrid_type,
      phone_number: session?.phone_number,
    })
    .then((response) => {
      return response.data; // Return the response data which may include events
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'bookAvailableTimeslots',
        data: { serviceUuid, timeslotUuids, clientUuid, oneOffUuid, guestEmails },
      });
      if (onError) onError(error);
      else throw error;
    });
  }

const existsFormTemplate = (id: uuid | string, onError?: (error?: any) => void): Promise<boolean | void> =>
  axios
    .get<IWizFormTemplateExists>(`${FORM_TEMPLATE_URL(id)}exists/`)
    .then((res) => !!res.data?.exists)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'existsFormTemplate',
        data: { id },
      });
      if (onError) onError(error);
      else throw error;
    });

const getFormTemplate = (id: uuid | string, onError?: (error?: any) => void): Promise<IWizFormTemplate> =>
  existsFormTemplate(id)
    .then((exists: boolean | void) => {
      if (exists) return axios.get(`${FORM_TEMPLATE_URL(id)}`).then((res) => res.data);
      else return undefined;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'getFormTemplate',
        data: { id },
      });
      if (onError) onError(error);
      else throw error;
    });

const getFormTemplates = (onError?: (error?: any) => void): Promise<Array<IWizFormTemplate>> => {
  return axios
    .get(FORM_TEMPLATES_URL)
    .then((res) => res.data)

    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'getFormTemplates',
      });
      if (onError) onError(error);
      else throw error;
    });
};

function useFormTemplates(props: {
  provider?: Provider;
  searchTerm?: string;
  enabled?: boolean;
  onError?: (error?: any) => void;
}) {
  return useQuery(
    ['form-templates', props.provider?.uuid, props.searchTerm || ''],
    async (): Promise<Array<IWizFormTemplate>> => {
      const { data } = await axios.get(`${FORM_TEMPLATES_URL}?search=${props.searchTerm || ''}`);
      return data;
    },
    {
      enabled: props.enabled !== false,
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useFormTemplates',
          data: { client: props.provider?.uuid, searchTerm: props.searchTerm, enabled: props.enabled },
        });
        if (props.onError) props.onError(error);
        else throw error;
      },
    }
  );
}

const inviteFormTemplate = (
  formTemplateUuid: uuid,
  clientUuid: ID,
  details?: any,
  onError?: (error?: any) => void
): Promise<ClientInvite | undefined> => {
  return axios
    .post(`${FORM_TEMPLATE_URL(formTemplateUuid)}invite/?client_uuid=${clientUuid}`, details)
    .then((response: AxiosResponse<ClientInvite>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'inviteFormTemplate',
        data: { clientUuid, formTemplateUuid, details },
      });
      if (onError) onError(error);
      else throw error;
      return undefined;
    });
};

const bulkInviteFormTemplate = (
  formTemplateUuid: uuid,
  clientUuids: string[],
  sendTo: 'client' | 'guardian' | 'both',
  onError?: (error?: any) => void
): Promise<ClientInvite[] | undefined> => {
  return axios
    .post(`${FORM_TEMPLATE_URL(formTemplateUuid)}bulk_invite/`, {
      client_uuids: clientUuids,
      send_to: sendTo,
    })
    .then((response: AxiosResponse<ClientInvite[]>) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'client._requests',
        func: 'bulkInviteFormTemplate',
        data: { clientUuids, formTemplateUuid, sendTo },
      });
      if (onError) onError(error);
      else throw error;
      return undefined;
    });
};

const createFormTemplate = (
  payload: Partial<IWizFormTemplate>,
  onError?: (error?: any) => void
): Promise<IWizFormTemplate> =>
  axios
    .post(FORM_TEMPLATES_URL, payload)
    .then((res) => res.data)
    .catch((error) => {
      monitoringCaptureError(error, { module: 'api', func: 'createFormTemplate', data: { payload } });
      if (onError) onError(error);
      else throw error;
    });

function useCreateFormTemplate() {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: Partial<IWizFormTemplate>) => createFormTemplate(payload),
    { onSuccess: () => queryClient.invalidateQueries('form-templates') }
  );
}

const updateFormTemplate = (
  uuid: uuid,
  patch: Partial<IWizFormTemplate>,
  onError?: (error?: any) => void
): Promise<IWizFormTemplate> =>
  axios
    .patch(`${FORM_TEMPLATE_URL(uuid)}`, patch)
    .then((res) => res.data)
    .catch((error) => {
      monitoringCaptureError(error, { module: 'api', func: 'updateFormTemplate', data: { uuid, patch } });
      if (onError) onError(error);
      else throw error;
    });

function useUpdateFormTemplate() {
  const queryClient = useQueryClient();
  return useMutation(
    ({ ft, patch }: { ft: IWizFormTemplate; patch: Partial<IWizFormTemplate> }) =>
      updateFormTemplate(ft.uuid, patch),
    { onSuccess: () => queryClient.invalidateQueries('form-templates') }
  );
}

const deleteFormTemplate = (
  uuid: uuid,
  onError?: (error?: any) => void
): Promise<void> =>
  axios
    .delete(`${FORM_TEMPLATE_URL(uuid)}`)
    .then(() => undefined)
    .catch((error) => {
      monitoringCaptureError(error, { module: 'api', func: 'deleteFormTemplate', data: { uuid } });
      if (onError) onError(error);
      else throw error;
    });

function useDeleteFormTemplate() {
  const queryClient = useQueryClient();
  return useMutation(
    (uuid: uuid) => deleteFormTemplate(uuid),
    { onSuccess: () => queryClient.invalidateQueries('form-templates') }
  );
}

const generateFormTemplateFromFile = (
  files: File[],
  onError?: (error?: any) => void
): Promise<{ uuid: string; ai_status: string; display_name: string }> => {
  const formData = new FormData();
  files.forEach((f) => formData.append('files', f));
  return axios
    .post(`${FORM_TEMPLATES_URL}generate_from_file/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((res) => res.data)
    .catch((error) => {
      monitoringCaptureError(error, { module: 'api', func: 'generateFormTemplateFromFile' });
      if (onError) onError(error);
      else throw error;
    });
};

const generateFormTemplateFromPrompt = (
  prompt: string,
  onError?: (error?: any) => void
): Promise<{ uuid: string; ai_status: string; display_name: string }> =>
  axios
    .post(`${FORM_TEMPLATES_URL}generate_from_prompt/`, { prompt })
    .then((res) => res.data)
    .catch((error) => {
      monitoringCaptureError(error, { module: 'api', func: 'generateFormTemplateFromPrompt', data: { prompt } });
      if (onError) onError(error);
      else throw error;
    });

const getFormTemplateGenerateStatus = (
  uuid: string,
): Promise<{ uuid: string; ai_status: string; ai_error_message?: string; display_name: string; has_draft: boolean }> =>
  axios
    .get(`${FORM_TEMPLATES_URL}${uuid}/generate_status/`)
    .then((res) => res.data);

const approveFormTemplateDraft = (uuid: string): Promise<IWizFormTemplate> =>
  axios
    .post(`${FORM_TEMPLATES_URL}${uuid}/approve_draft/`)
    .then((res) => res.data);

const createForm = (form: IWizForm, onError?: (error?: any) => void): Promise<IWizForm> =>
  axios
    .post(`${FORM_URL}`, form)
    .then((res) => res.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'createForm',
        data: { form },
      });
      if (onError) onError(error);
      else throw error;
    });

const updateForm = (uuid: uuid, form: IWizForm, onError?: (error?: any) => void): Promise<IWizForm> =>
  axios
    .patch(`${FORM_URL}${uuid}/`, form)
    .then((res) => res.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'updateForm',
        data: { uuid, form },
      });
      if (onError) onError(error);
      else throw error;
    });

const getIntake = (inviteId: uuid, onError?: (error?: any) => void) =>
  axios
    .get(`${API_URL}/intake/${inviteId}/`)
    .then((res) => res.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'existsFormTemplate',
        data: { inviteId },
      });
      if (onError) onError(error);
      else throw error;
    });

const postIntake = (inviteId: uuid | string, formData: any, onError?: (error?: any) => void) =>
  axios.post(`${API_URL}/intake/${inviteId}/`, formData).catch((error) => {
    monitoringCaptureError(error, {
      module: 'api',
      func: 'postIntake',
      data: { inviteId, formData },
    });
    if (onError) onError(error);
    else throw error;
  });

function useNotes(client?: Client, searchTerm?: string, enabled?: boolean, onError?: (error?: any) => void) {
  return useQuery(
    ['notes', client?.uuid, searchTerm || ''],
    async (): Promise<Array<Note>> => {
      const { data } = await axios.get(`${API_URL}/clients/${client?.uuid}/notes/?search=${searchTerm || ''}`);
      return data;
    },
    {
      enabled: enabled !== false && !!client,
      refetchOnWindowFocus: false,
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useNotes',
          data: { client: client?.uuid, searchTerm, enabled },
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

function getNote(client?: Client, note_uuid?: uuid, onError?: (error?: any) => void) {
  return axios.get(`${API_URL}/clients/${client?.uuid}/notes/${note_uuid}/`).then((res) => res.data);
}

// Create a note (returns Promise<Note>)
export function createNote(client: Client, note: Note): Promise<Note> {
  return axios.post(`${API_URL}/clients/${client.uuid}/notes/`, note).then(res => res.data);
}

// Update a note (returns Promise<Note>)
export function updateNote(client: Client, note: Note): Promise<Note> {
  return axios.patch(`${API_URL}/clients/${client.uuid}/notes/${note?.uuid}/`, note).then(res => res.data);
}

// Delete a note (returns Promise<void>)
export function deleteNote(client: Client, note: Note): Promise<void> {
  return axios.delete(`${API_URL}/clients/${client.uuid}/notes/${note?.uuid}/`).then(() => {});
}

function useCreateNote(client: Client, onSuccess?: (note: Note) => void, onError?: (error?: any) => void) {
  const queryClient = useQueryClient();
  return useMutation(
    (note: Note) => {
      return createNote(client, note);
    },
    {
      onSuccess: (updatedItem) => {
        queryClient.invalidateQueries('notes');
        if (onSuccess) onSuccess(updatedItem);
      },
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useCreateNote',
          data: { client: client?.uuid },
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

function useUpdateNote(client: Client, onSuccess?: (note: Note) => void, onError?: (error?: any) => void) {
  const queryClient = useQueryClient();
  return useMutation(
    (note?: Note) => {
      return updateNote(client, note!);
    },
    {
      onSuccess: (updatedItem) => {
        queryClient.invalidateQueries('notes');
        if (onSuccess) onSuccess(updatedItem);
      },
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useUpdateNote',
          data: { client: client?.uuid },
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

/**
 * Autosave draft content for a note (does not overwrite main content).
 */
export function saveNoteDraft(client: Client, noteUuid: uuid, draft_content: string) {
  return axios.patch(`${API_URL}/clients/${client.uuid}/notes/${noteUuid}/`, { draft_content });
}

export function linkNoteToEventLog(eventLogUuid: uuid, noteUuid: uuid) {
  return axios.patch(`${API_URL}/events-log/${eventLogUuid}/`, {
    note: { uuid: noteUuid },
  });
}

export function unlinkNoteFromEventLog(eventLogUuid: uuid) {
  return axios.patch(`${API_URL}/events-log/${eventLogUuid}/`, {
    note: null,
  });
}

function useDocuments(props: {
  client?: Client;
  provider?: Provider;
  searchTerm?: string;
  enabled?: boolean;
  onError?: (error?: any) => void;
}) {
  return useQuery(
    ['documents', props.client?.uuid, props.searchTerm || ''],
    async (): Promise<Array<Document>> => {
      const { data } = await axios.get(
        props.client
          ? `${API_URL}/clients/${props.client?.uuid}/documents/?search=${props.searchTerm || ''}`
          : `${API_URL}/providers/${props.provider?.uuid}/documents/?search=${props.searchTerm || ''}`
      );
      return data;
    },
    {
      enabled: props.enabled !== false && (!!props.client || !!props.provider),
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useDocuments',
          data: { client: props.client?.uuid, searchTerm: props.searchTerm, enabled: props.enabled },
        });
        if (props.onError) props.onError(error);
        else throw error;
      },
    }
  );
}

function getDocument(props: { doc_uuid: uuid }): Promise<Document | undefined> {
  return axios.get(`${API_URL}/documents/${props.doc_uuid}/`).then((res) => res.data).catch((error) => {
    monitoringCaptureError(error, {
      module: 'api',
      func: 'getDocument',
      data: { doc_uuid: props.doc_uuid },
    });
    return undefined;
  });
}

function createDocument(props: {
  doc: Document;
  client?: Client;
  provider?: Provider;
  onError?: (error?: any) => void;
}) {
  const formData = new FormData();
  const doc = props.doc;
  if (doc.file) formData.append('file', doc.file);
  if (doc.description) formData.append('description', doc.description);
  if (doc.file?.name) formData.append('filename', doc.file.name);
  if (doc.file?.type) formData.append('filetype', doc.file.type);
  if (doc.file?.size) formData.append('filesize', `${doc.file.size}`);
  if (doc.file?.lastModified) formData.append('filemodified', `${new Date(doc.file.lastModified).toISOString()}`);
  if (doc.note) formData.append('note', doc.note);
  if (doc.provider?.uuid?.toString()) formData.append('provider_uuid', doc.provider?.uuid?.toString() || '');
  if (doc.client?.uuid?.toString()) formData.append('client_uuid', doc.client?.uuid?.toString() || '');

  return axios.post(
    props.client
      ? `${API_URL}/clients/${props.client.uuid}/documents/`
      : `${API_URL}/providers/${props.provider?.uuid}/documents/`,
    formData
  );
}

function useCreateDocument(props: {
  doc: Document;
  client?: Client;
  provider?: Provider;
  onSuccess?: (doc: Document) => void;
  onError?: (error?: any) => void;
}) {
  const queryClient = useQueryClient();
  return useMutation(
    () => {
      return createDocument({ doc: props.doc, client: props.client, provider: props.provider });
    },
    {
      onSuccess: (updatedItem) => {
        queryClient.invalidateQueries('documents');
        if (props.onSuccess) props.onSuccess(updatedItem.data);
      },
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useCreateDocument',
          data: { client: props.client?.uuid, doc: props.doc },
        });
        if (props.onError) props.onError(error);
        else throw error;
      },
    }
  );
}

function updateDocument(props: { doc?: Document; client?: Client; provider?: Provider }) {
  return axios.patch(
    props.client
      ? `${API_URL}/clients/${props.client.uuid}/documents/${props.doc?.uuid}/`
      : `${API_URL}/providers/${props.provider?.uuid}/documents/${props.doc?.uuid}/`,
    props.doc
  );
}

function useUpdateDocument(props: {
  client?: Client;
  provider?: Provider;
  onSuccess?: (doc: Document) => void;
  onError?: (error?: any) => void;
}) {
  const queryClient = useQueryClient();
  return useMutation(
    (doc?: Document) => {
      return updateDocument({ doc: doc, client: props.client, provider: props.provider });
    },
    {
      onSuccess: (updatedItem) => {
        queryClient.invalidateQueries('documents');
        if (props.onSuccess) props.onSuccess(updatedItem.data);
      },
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useUpdateDocument',
          data: { client: props.client?.uuid },
        });
        if (props.onError) props.onError(error);
        else throw error;
      },
    }
  );
}

function getDocumentThumbnailUrl(props: {
  doc?: Document;
  client?: Client;
  provider?: Provider;
  onError?: (error?: any) => void;
}) {
  return axios
    .get(
      props.client
        ? `${API_URL}/clients/${props.client.uuid}/documents/${props.doc?.uuid}/thumbnail/`
        : props.provider ? `${API_URL}/providers/${props.provider?.uuid}/documents/${props.doc?.uuid}/thumbnail/` 
        : `${API_URL}/documents/${props.doc?.uuid}/thumbnail/`,
      { responseType: 'arraybuffer' }
    )
    .then((response) => {
      const contentType = response.headers['content-type'];
      const data = new Blob([response.data], { type: contentType });
      const url = URL.createObjectURL(data);
      return url;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'getDocumentThumbnailUrl',
        data: { client: props.client?.uuid, doc: props.doc?.uuid },
      });
      if (props.onError) props.onError(error);
      else throw error;
    });
}

// function downloadDocument(client: Client, doc?: Document) {
//   return axios
//     .get(`${API_URL}/clients/${client.uuid}/documents/${doc?.uuid}/download/`, {
//       responseType: 'blob',
//     })
//     .then((response) => {
//       const url = window.URL.createObjectURL(new Blob([response.data]));
//       const link = document.createElement('a');
//       link.href = url;
//       link.setAttribute('download', doc?.filename || 'file');
//       document.body.appendChild(link);
//       link.click();
//     })
//     .catch((error) => {
//       console.error(error);
//     });
// }

// function openDocument(client: Client, doc?: Document, newWindow?: Window | null) {
//   return axios
//     .get(`${API_URL}/clients/${client.uuid}/documents/${doc?.uuid}/open/`, {
//       responseType: 'blob',
//     })
//     .then((response) => {
//       if (newWindow) {
//         const objectURL = URL.createObjectURL(new Blob([response.data], { type: doc?.filetype }));
//         newWindow.location.href = objectURL;
//         newWindow.document.title = doc?.filename || 'file';
//       }
//     })
//     .catch((error) => {
//       console.error(error);
//     });
// }

function openDocument(props: {
  doc?: Document;
  client?: Client;
  provider?: Provider;
  newWindow?: Window | null;
  onError?: (error?: any) => void;
}) {
  return axios
    .get(
      props.client
        ? `${API_URL}/clients/${props.client.uuid}/documents/${props.doc?.uuid}/token/`
        : props.provider ? `${API_URL}/providers/${props.provider?.uuid}/documents/${props.doc?.uuid}/token/`
        : `${API_URL}/documents/${props.doc?.uuid}/token/`,
    )
    .then((response) => {
      const token = response.data.token;
      const url = `${API_URL}/documents/open/${token}/${props.doc?.filename}`;
      if (props.newWindow) props.newWindow.location.href = url;
      else window.open(url, '_blank', 'noreferrer');
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'openDocument',
        data: { client: props.client?.uuid, doc: props.doc?.uuid },
      });
      if (props.onError) props.onError(error);
      else throw error;
    });
}

function downloadDocument(props: {
  doc?: Document;
  client?: Client;
  provider?: Provider;
  onError?: (error?: any) => void;
}) {
  return axios
    .get(
      props.client
        ? `${API_URL}/clients/${props.client.uuid}/documents/${props.doc?.uuid}/token/`
        : props.provider ? `${API_URL}/providers/${props.provider?.uuid}/documents/${props.doc?.uuid}/token/`
        : `${API_URL}/documents/${props.doc?.uuid}/token/`,
    )
    .then((response) => {
      const token = response.data.token;
      const url = `${API_URL}/documents/download/${token}/${props.doc?.filename}`;
      window.open(url, '_self', 'noreferrer');
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'downloadDocument',
        data: { client: props.client?.uuid, doc: props.doc?.uuid },
      });
      if (props.onError) props.onError(error);
      else throw error;
    });
}

function downloadInvoice(props: {
  invoice: IInvoice;
  client?: Client;
  provider?: Provider;
  onError?: (error?: any) => void;
}) {
  const url = props.client
    ? `${API_URL}/clients/${props.client.uuid}/invoices/${props.invoice.uuid}/pdf/`
    : `${API_URL}/providers/${props.provider?.uuid}/invoices/${props.invoice.uuid}/pdf/`;
  // window.open(url, '_self', 'noreferrer');
  axios
    .get(url, {
      responseType: 'blob',
    })
    .then((res) => {
      fileDownload(res.data, 'Invoice-' + props.invoice.invoice_number + '.pdf');
    });
}

function downloadClientInvoices(props: {
  clientUuid: uuid;
  start: string; // YYYY-MM-DD
  end: string; // YYYY-MM-DD
  filename?: string;
  onError?: (error?: any) => void;
}) {
  const url = `${API_URL}/clients/${props.clientUuid}/invoices/batch-download/`;
  return axios
    .get(url, {
      params: { start: props.start, end: props.end },
      responseType: 'blob',
    })
    .then((res) => {
      const filename = props.filename || `invoices-${props.start}-to-${props.end}.zip`;
      fileDownload(res.data, filename);
    })
    .catch((error) => {
      if (props.onError) props.onError(error);
      else throw error;
    });
}

function downloadSuperBill(props: {
  superBill: ISuperBill;
  client?: Client;
  provider?: Provider;
  onError?: (error?: any) => void;
}) {
  const url = `${API_URL}/clients/${props.client?.uuid}/superbills/${props.superBill.uuid}/pdf/`;
  axios
    .get(url, {
      responseType: 'blob',
    })
    .then((res) => {
      fileDownload(res.data, 'SuperBill-' + props.superBill.statement_number + '.pdf');
    });
}

function downloadSuperBillPreview(superbill: ISuperBill) {
  return axios
    .post(`${API_URL}/clients/${superbill?.client?.uuid}/superbills/preview/`, superbill, {
      responseType: 'blob',
    })
    .then((res) => {
      fileDownload(res.data, 'SuperBill-preview.pdf');
    });
}

function uploadImage(props: {
  image: Blob;
  filename: string;
  client?: Client;
  provider?: Provider;
  onError?: (error?: any) => void;
}) {
  const formData = new FormData();
  formData.append('image', props.image, props.filename);
  if (props.provider?.uuid?.toString()) formData.append('provider_uuid', props.provider?.uuid?.toString() || '');
  if (props.client?.uuid?.toString()) formData.append('client_uuid', props.client?.uuid?.toString() || '');

  return axios.post(`${API_URL}/imgs/`, formData);
}

function imageUrl(uuid: string) {
  return `${API_URL}/imgs/${uuid}/img/`;
}

const getInvoices = (filters: any): Promise<Array<IInvoice>> => {
  return axios
    .get(`${API_URL}/invoices/`, { params: filters })
    .then((res) => res.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'getInvoices',
      });
      throw error;
    });
};

const getStripeInvoices = (filters: any): Promise<Array<IInvoice>> => {
  return axios
    .get(`${API_URL}/sinvoices/`, { params: filters })
    .then((res) => res.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'getInvoices',
      });
      throw error;
    });
};

function downloadInvoiceReceipt(props: {
  invoice: IInvoice;
  client?: Client;
  provider?: Provider;
  onError?: (error?: any) => void;
}) {
  const url = props.client
    ? `${API_URL}/clients/${props.client.uuid}/invoices/${props.invoice.uuid}/receipt-pdf/`
    : `${API_URL}/providers/${props.provider?.uuid}/invoices/${props.invoice.uuid}/receipt-pdf/`;
  // window.open(url, '_self', 'noreferrer');
  axios
    .get(url, {
      responseType: 'blob',
    })
    .then((res) => {
      fileDownload(res.data, 'Receipt-' + props.invoice.invoice_number + '.pdf');
    });
}

function deleteDocument(props: {
  doc?: Document;
  client?: Client;
  provider?: Provider;
  onSuccess?: () => void;
  onError?: (error?: any) => void;
}) {
  return axios
    .delete(
      props.client
        ? `${API_URL}/clients/${props.client.uuid}/documents/${props.doc?.uuid}/`
        : `${API_URL}/providers/${props.provider?.uuid}/documents/${props.doc?.uuid}/`
    )
    .then((response) => {
      props.onSuccess && props.onSuccess();
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'deleteDocument',
        data: { client: props.client?.uuid, provider: props.provider?.uuid, doc: props.doc?.uuid },
      });
      if (props.onError) props.onError(error);
      else throw error;
    });
}

function bulkShareDocument(docUuid: string, clientUuids: string[]): Promise<{ created: string[] }> {
  return axios
    .post(`${API_URL}/documents/${docUuid}/bulk-share/`, { clients: clientUuids })
    .then((res) => res.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'bulkShareDocument',
        data: { docUuid, clientUuids },
      });
      throw error;
    });
}

function useNoteTemplates(provider?: Provider, searchTerm?: string) {
  return useQuery(
    ['notes', provider?.uuid],
    async (): Promise<Array<INoteTemplate>> => {
      const { data } = await axios.get(
        provider?.uuid
          ? `${API_URL}/providers/${provider.uuid}/note-templates/?search=${searchTerm || ''}`
          : `${API_URL}/note-templates/?search=${searchTerm || ''}`
      );
      return data;
    },
    {
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useNoteTemplates',
          data: { provider: provider?.uuid, searchTerm },
        });
        throw error;
      },
    }
  );
}

function createNoteTemplate(noteTemplate: INoteTemplate) {
  return axios
    .post(`${API_URL}/providers/${noteTemplate?.provider?.uuid}/note-templates/`, noteTemplate)
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'createNoteTemplate',
        data: { noteTemplate },
      });
      throw error;
    });
}

function updateNoteTemplate(noteTemplate: INoteTemplate, update: INoteTemplate) {
  return axios
    .patch(`${API_URL}/note-templates/${noteTemplate.uuid}/`, update)
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'updateNoteTemplate',
        data: { noteTemplate, update },
      });
      throw error;
    });
}

function deleteNoteTemplate(noteTemplate: INoteTemplate) {
  return axios
    .delete(`${API_URL}/note-templates/${noteTemplate.uuid}/`)
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'deleteNoteTemplate',
        data: { noteTemplate },
      });
      throw error;
    });
}

function useExpenses(props: {
  provider?: Provider;
  searchTerm?: string;
  startDate?: Date | null;
  endDate?: Date | null;
  providersUuids?: Array<uuid>;
  client?: Client;
  tagUuids?: string[];
}) {
  const { provider, searchTerm, startDate, endDate, providersUuids, client, tagUuids } = props;
  const query = qs.stringify(
    {
      search: searchTerm,
      start_date: startDate,
      end_date: endDate,
      providers: providersUuids,
      client: client?.uuid,
      filter_tags: tagUuids?.length ? tagUuids.join(',') : undefined,
    },
    { skipNulls: true }
  );
  return useQuery(
    [
      'expenses',
      provider?.uuid,
      startDate,
      endDate,
      searchTerm,
      providersUuids,
      client?.uuid,
      tagUuids,
    ],
    async (): Promise<Array<IExpense>> => {
      const { data } = await axios.get(`${API_URL}/providers/${provider?.uuid}/expenses/?${query}`);
      return data;
    },
    {
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useExpenses',
          data: { provider: provider?.uuid, searchTerm },
        });
        throw error;
      },
    }
  );
}

function createExpense(expense: IExpense) {
  return axios
    .post(`${API_URL}/providers/${expense?.provider?.uuid}/expenses/`, expense)
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'createExpense',
        data: { expense },
      });
      throw error;
    });
}

function updateExpense(expense: IExpense, update: IExpense) {
  return axios
    .patch(`${API_URL}/expenses/${expense.uuid}/`, update)
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'updateExpense',
        data: { expense, update },
      });
      throw error;
    });
}

function deleteExpense(expense: IExpense) {
  return axios
    .delete(`${API_URL}/expenses/${expense.uuid}/`)
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'deleteExpense',
        data: { expense },
      });
      throw error;
    });
}

function useSuperBills(props: {
  client?: Client;
  searchTerm?: string;
  startDate?: Date | null;
  endDate?: Date | null;
  providersUuids?: Array<uuid>;
}) {
  const { searchTerm, startDate, endDate, providersUuids, client } = props;
  const query = qs.stringify(
    { search: searchTerm, start_date: startDate, end_date: endDate, providers: providersUuids, client: client?.uuid },
    { skipNulls: true }
  );
  return useQuery(
    ['superbills', client?.uuid],
    async (): Promise<Array<ISuperBill>> => {
      const { data } = await axios.get(`${API_URL}/clients/${client?.uuid}/superbills/?${query}`);
      return data;
    },
    {
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useSuperBills',
          data: { client: client?.uuid, searchTerm },
        });
        throw error;
      },
    }
  );
}

function getSuperBill(superBillUuid: uuid) {
  return axios
    .get(`${API_URL}/superbills/${superBillUuid}/`)
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'getSuperBill',
        data: { superBillUuid },
      });
      throw error;
    });
}

function createSuperBill(superbill: ISuperBill) {
  return axios
    .post(`${API_URL}/clients/${superbill?.client?.uuid}/superbills/`, superbill)
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'createSuperBill',
        data: { superbill },
      });
      throw error;
    });
}

function deleteSuperBill(superbill: ISuperBill) {
  return axios
    .delete(`${API_URL}/superbills/${superbill.uuid}/`)
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'deleteSuperBill',
        data: { superbill },
      });
      throw error;
    });
}

function useIcsIntegrations(props: { provider?: Provider; onError?: (error?: any) => void }) {
  return useQuery(
    ['ics-integrations', props?.provider?.uuid],
    async (): Promise<Array<IIcsIntegration>> => {
      return await axios.get(`${API_URL}/integrations/ics/`).then((res) => res.data);
    },
    {
      enabled: !!props?.provider,
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useIcsIntegrations',
          data: { provider: props.provider?.uuid },
        });
        if (props?.onError) props?.onError(error);
        else throw error;
      },
    }
  );
}

const createIcsIntegration = (props: {
  ics: IIcsIntegration;
  onError?: (error?: any) => void;
}): Promise<IIcsIntegration> =>
  axios
    .post(`${API_URL}/integrations/ics/`, props.ics)
    .then((res) => res.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'createIcsIntegration',
        data: { ics: props.ics },
      });
      if (props.onError) props.onError(error);
      else throw error;
    });

const updateIcsIntegration = (props: {
  ics: IIcsIntegration;
  onError?: (error?: any) => void;
}): Promise<IIcsIntegration> =>
  axios
    .patch(`${API_URL}/integrations/ics/${props.ics.uuid}`, props.ics)
    .then((res) => res.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'updateIcsIntegration',
        data: { ics: props.ics },
      });
      if (props.onError) props.onError(error);
      else throw error;
    });

const refreshIcsIntegration = (props: {
  ics: IIcsIntegration;
  onError?: (error?: any) => void;
}): Promise<IIcsIntegration> =>
  axios
    .get(`${API_URL}/integrations/ics/${props.ics.uuid}/refresh/`, props.ics)
    .then((res) => res.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'refreshIcsIntegration',
        data: { ics: props.ics },
      });
      if (props.onError) props.onError(error);
      else throw error;
    });

const deleteIcsIntegration = (props: { ics: IIcsIntegration; onError?: (error?: any) => void }): Promise<boolean> =>
  axios
    .delete(`${API_URL}/integrations/ics/${props.ics.uuid}`, props.ics)
    .then((res) => {
      return true;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'deleteIcsIntegration',
        data: { ics: props.ics },
      });
      if (props.onError) props.onError(error);
      return false;
    });

const ping = async () => {
  const ret = await axios.get(`${API_URL}/ping`);
  return ret && ret.status == 200;
};

const guestPaymentSetup = (
  email: string | undefined,
  name: string,
  timezone: string,
  invite?: string,
  guardianName?: string,
  guardianEmail?: string
) => {
  return axios
    .post(`${API_URL}/clients/gpinfo/`, {
      name,
      email,
      timezone,
      invite,
      guardian_name: guardianName,
      guardian_email: guardianEmail,
    })
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'guestPaymentSetup',
        data: { name, email, invite, guardianName, guardianEmail },
      });
      throw error;
    });
};

const clientPaymentSetup = (client?: Client, guardian?: Guardian, invite?: string, providerServiceUuid?: string) => {
  return axios
    .post(`${API_URL}/clients/pinfo/`, { client: client?.uuid, guardian: guardian?.uuid, invite: invite, provider_service_uuid: providerServiceUuid })
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'clientPaymentSetup',
        data: { client: client?.uuid, guardian: guardian?.uuid, invite: invite, provider_service_uuid: providerServiceUuid },
      });
      throw error;
    });
};

function useDatasets(type: string, codeSearch?: string, minLength?: number, onError?: (error?: any) => void) {
  return useQuery(
    ['datasets', type, codeSearch],
    async (): Promise<Array<Dataset>> => {
      return await getDatasets(type, codeSearch, onError);
    },
    {
      cacheTime: 24 * 60 * 60 * 1000, // Cache for 24 hours
      enabled: !!type && (minLength === undefined || (!!codeSearch && codeSearch.length >= minLength)), // Only run if type is provided and either no search term or at least 2 chars
    }
  );
}

function getDatasets(type: string, codeSearch?: string, onError?: (error?: any) => void): Promise<Array<Dataset>> {
  // Only add search parameter if at least 2 characters provided
  const searchQuery = codeSearch && codeSearch.length >= 2 ? `?search=${codeSearch}` : '';

  return axios
    .get(`${DATASETS_URL(type)}${searchQuery}`)
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'getDatasets',
        data: { type, codeSearch },
      });
      if (onError) onError(error);
      throw error;
    });
}

function useLocations(providerUuid?: uuid, onError?: (error?: any) => void) {
  return useQuery(
    ['locations', providerUuid],
    async (): Promise<Array<Location>> => {
      // Assuming locations are fetched per clinic/provider if clinicUuid is provided
      // Adjust the URL and params as needed based on your backend API structure for locations
      const url = providerUuid ? `${PROVIDER_URL}${providerUuid}/locations/` : LOCATIONS_URL;
      const { data } = await axios.get(url);
      return data;
    },
    {
      enabled: !!providerUuid, // Example: only fetch if clinicUuid is provided
      refetchOnWindowFocus: false,
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useLocations',
          data: { providerUuid },
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

function getLocations(providerUuid?: uuid, onError?: (error?: any) => void): Promise<Array<Location>> {
  const url = providerUuid ? `${PROVIDER_URL}${providerUuid}/locations/` : LOCATIONS_URL;
  return axios
    .get(url)
    .then((res) => {
      return res.data;
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'getLocations',
        data: { providerUuid },
      });
      if (onError) onError(error);
      throw error; // Added throw
    });
}

function updateLocation(
  providerUuid: uuid,
  locationUuid: uuid,
  data: Partial<Location>,
  onError?: (error?: any) => void
): Promise<Location> {
  const url = providerUuid ? `${PROVIDER_URL}${providerUuid}/locations/` : LOCATIONS_URL;
  return axios
    .patch(`${url}${locationUuid}/`, data)
    .then((res) => res.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'updateLocation',
        data: { locationUuid, data },
      });
      if (onError) onError(error);
      throw error;
    });
}

function useUpdateLocation(onSuccess?: (location: Location) => void, onError?: (error?: any) => void) {
  const queryClient = useQueryClient();
  return useMutation(
    (variables: { providerUuid: uuid; locationUuid: uuid; data: Partial<Location> }) =>
      updateLocation(variables.providerUuid, variables.locationUuid, variables.data, onError),
    {
      onSuccess: (updatedLocation) => {
        queryClient.invalidateQueries(['locations', updatedLocation.uuid]); // Invalidate specific location
        queryClient.invalidateQueries('locations'); // Invalidate list of locations
        // Potentially invalidate clinic data if locations are nested or affect clinic display
        // queryClient.invalidateQueries(['providers', clinic?.uuid])
        // queryClient.invalidateQueries(['clinic', clinic?.uuid])
        if (onSuccess) onSuccess(updatedLocation);
      },
      onError: (error, variables) => {
        // Error already logged by updateLocation, additional logging if needed
        if (onError) onError(error);
        // No explicit throw here as updateLocation already throws
      },
    }
  );
}

// Create Location
function createLocation(
  providerUuid: uuid,
  locationData: Partial<Location>,
  onError?: (error?: any) => void
): Promise<Location> {
  const url = providerUuid ? `${PROVIDER_URL}${providerUuid}/locations/` : LOCATIONS_URL;
  return axios
    .post(url, { ...locationData }) // Assuming API expects provider Uuid in the body
    .then((res) => res.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'createLocation',
        data: { providerUuid, locationData },
      });
      if (onError) onError(error);
      throw error;
    });
}

function useCreateLocation(onSuccess?: (location: Location) => void, onError?: (error?: any) => void) {
  const queryClient = useQueryClient();
  return useMutation(
    (variables: { providerUuid: uuid; locationData: Partial<Location> }) =>
      createLocation(variables.providerUuid, variables.locationData, onError),
    {
      onSuccess: (newLocation, variables) => {
        // Invalidate locations for the specific clinic using clinicUuid from variables
        queryClient.invalidateQueries(['locations', variables.providerUuid]);
        // Potentially invalidate general locations list if not clinic-specific or if a general list is used elsewhere
        // queryClient.invalidateQueries('locations');
        if (onSuccess) onSuccess(newLocation);
      },
      onError: (error, variables) => {
        if (onError) onError(error);
      },
    }
  );
}

// Delete Location
function deleteLocation(providerUuid: uuid, locationUuid: uuid, onError?: (error?: any) => void): Promise<boolean> {
  const url = providerUuid ? `${PROVIDER_URL}${providerUuid}/locations/` : LOCATIONS_URL;
  return axios
    .delete(`${url}${locationUuid}/`)
    .then(() => true)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'deleteLocation',
        data: { providerUuid, locationUuid },
      });
      if (onError) onError(error);
      throw error;
    });
}

function useDeleteLocation(
  providerUuid: uuid,
  onSuccess?: (locationUuid: uuid) => void,
  onError?: (error?: any) => void
) {
  const queryClient = useQueryClient();
  return useMutation((locationUuid: uuid) => deleteLocation(providerUuid, locationUuid, onError), {
    onSuccess: (result, locationUuid) => {
      // It's tricky to get clinicUuid here directly for invalidation if not passed.
      // A broader invalidation might be simplest, or pass clinicUuid if available.
      queryClient.invalidateQueries('locations'); // Broad invalidation
      if (onSuccess) onSuccess(locationUuid);
    },
    onError: (error, variables) => {
      if (onError) onError(error);
    },
  });
}

// Notification Settings API
function updateClientNotifications(
  clientUuid: uuid,
  notifications: any,
  onError?: (error?: any) => void
): Promise<Client> {
  return axios
    .patch(`${API_URL}/clients/${clientUuid}/notifications/`, { notifications })
    .then((response) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'updateClientNotifications',
        data: { clientUuid, notifications },
      });
      if (onError) onError(error);
      throw error;
    });
}

function useUpdateClientNotifications(
  onSuccess?: (client: Client) => void,
  onError?: (error?: any) => void
) {
  const queryClient = useQueryClient();
  return useMutation(
    (variables: { clientUuid: uuid; notifications: any }) =>
      updateClientNotifications(variables.clientUuid, variables.notifications, onError),
    {
      onSuccess: (client, variables) => {
        queryClient.invalidateQueries(['clients', variables.clientUuid]);
        queryClient.invalidateQueries('clients');
        if (onSuccess) onSuccess(client);
      },
      onError: (error) => {
        if (onError) onError(error);
      },
    }
  );
}

function updateGuardianNotifications(
  clientUuid: uuid,
  guardianUuid: uuid,
  notifications: any,
  onError?: (error?: any) => void
): Promise<Guardian> {
  return axios
    .patch(`${API_URL}/clients/${clientUuid}/guardians/${guardianUuid}/notifications/`, { notifications })
    .then((response) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'updateGuardianNotifications',
        data: { clientUuid, guardianUuid, notifications },
      });
      if (onError) onError(error);
      throw error;
    });
}

function useUpdateGuardianNotifications(
  onSuccess?: (guardian: Guardian) => void,
  onError?: (error?: any) => void
) {
  const queryClient = useQueryClient();
  return useMutation(
    (variables: { clientUuid: uuid; guardianUuid: uuid; notifications: any }) =>
      updateGuardianNotifications(variables.clientUuid, variables.guardianUuid, variables.notifications, onError),
    {
      onSuccess: (guardian, variables) => {
        queryClient.invalidateQueries(['guardians', variables.guardianUuid]);
        queryClient.invalidateQueries(['clients', variables.clientUuid]);
        queryClient.invalidateQueries('guardians');
        if (onSuccess) onSuccess(guardian);
      },
      onError: (error) => {
        if (onError) onError(error);
      },
    }
  );
}

function updateProviderNotifications(
  providerUuid: uuid,
  notifications: any,
  onError?: (error?: any) => void
): Promise<Provider> {
  return axios
    .patch(`${API_URL}/providers/${providerUuid}/notifications/`, { notifications })
    .then((response) => response.data)
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'updateProviderNotifications',
        data: { providerUuid, notifications },
      });
      if (onError) onError(error);
      throw error;
    });
}

function useUpdateProviderNotifications(
  onSuccess?: (provider: Provider) => void,
  onError?: (error?: any) => void
) {
  const queryClient = useQueryClient();
  return useMutation(
    (variables: { providerUuid: uuid; notifications: any }) =>
      updateProviderNotifications(variables.providerUuid, variables.notifications, onError),
    {
      onSuccess: (provider, variables) => {
        queryClient.invalidateQueries(['providers', variables.providerUuid]);
        queryClient.invalidateQueries('providers');
        if (onSuccess) onSuccess(provider);
      },
      onError: (error) => {
        if (onError) onError(error);
      },
    }
  );
}

function usePaymentsReport(props: {
  start?: string | null;
  end?: string | null;
  providerUuid?: string | null;
  clientUuid?: string | null;
  runToken?: number | null;
  enabled?: boolean;
  onError?: (error?: any) => void;
}) {
  const { start, end, providerUuid, clientUuid, runToken, enabled, onError } = props;
  const params = new URLSearchParams();
  start && params.append('start', start);
  end && params.append('end', end);
  providerUuid && params.append('provider_uuid', providerUuid);
  clientUuid && params.append('client_uuid', clientUuid);

  return useQuery(
    ['payments-report', { start, end }, providerUuid, clientUuid, runToken],
    async (): Promise<any> => {
      const { data } = await axios.get(`${API_URL}/payments-report/?${params.toString()}`);
      return data;
    },
    {
      enabled: !!start && !!end && enabled !== false,
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 10,
      refetchOnWindowFocus: false,
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'usePaymentsReport',
          data: { start, end, providerUuid, clientUuid },
        });
        if (onError) onError(error);
        else throw error;
      },
    }
  );
}

function useActivityReport(props: {
  start?: string | null;
  end?: string | null;
  clientUuid?: string | null;
  providerUuids?: string[];
  categories?: string[];
  tagUuids?: string[];
  enabled?: boolean;
}) {
  const { start, end, clientUuid, providerUuids, categories, tagUuids, enabled } = props;
  const params = new URLSearchParams();
  start && params.append('start', start);
  end && params.append('end', end);
  clientUuid && params.append('client', clientUuid);
  providerUuids?.length && params.append('providers', providerUuids.join(','));
  categories?.length && params.append('category', categories.join(','));
  tagUuids?.length && params.append('filter_tags', tagUuids.join(','));

  return useQuery(
    ['activity-report', { start, end }, clientUuid, providerUuids, categories, tagUuids],
    async (): Promise<any[]> => {
      const { data } = await axios.get(`${API_URL}/activity-report/?${params.toString()}`);
      return data;
    },
    {
      enabled: !!start && !!end && enabled !== false,
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 10,
      refetchOnWindowFocus: false,
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useActivityReport',
          data: { start, end, clientUuid, providerUuids, categories },
        });
      },
    }
  );
}

export type FormResponsesReportParams = {
  template?: string;
  statuses?: string[];
  start?: string;
  end?: string;
  providerUuids?: string[];
  // Empty or absent = all clients (unlike providerUuids, where [] means none).
  clientUuids?: string[];
};

export type FormResponseRow = {
  id: string;
  client_uuid: string | null;
  client_name: string;
  client_email: string;
  provider_name: string;
  status: 'Completed' | 'Opened' | 'Sent';
  // The pending invite behind a Sent/Opened row; null once completed.
  invite_uuid: string | null;
  // The axios dates interceptor turns 'YYYY-MM-DD' strings into Dates.
  sent: string | Date;
  opened: string | Date;
  completed: string | Date;
  answers: Record<string, string | Date>;
};

// Everything the clinic's clients answered, system templates (e.g. the Wiz intake) included, which
// /form-templates/ does not list. The server sorts it alphabetically; keep that order.
export type FormResponsesReportTemplate = { uuid: string; name: string };

export type FormResponsesReport = {
  template: { uuid: string; name: string };
  columns: Array<{ key: string; label: string }>;
  rows: FormResponseRow[];
};

const formResponsesReportUrl = (p: FormResponsesReportParams, exportAs?: 'csv' | 'xlsx') => {
  const params = new URLSearchParams();
  p.template && params.append('template', p.template);
  p.statuses?.length && params.append('status', p.statuses.join(','));
  p.start && params.append('start', p.start);
  p.end && params.append('end', p.end);
  // [] is sent as providers= ("(None)" selected, so no rows); undefined = all.
  p.providerUuids && params.append('providers', p.providerUuids.join(','));
  p.clientUuids?.length && params.append('clients', p.clientUuids.join(','));
  exportAs && params.append('export', exportAs);
  return `${API_URL}/form-responses-report/?${params.toString()}`;
};

function useFormResponsesReport(props: FormResponsesReportParams) {
  return useQuery(
    ['form-responses-report', props],
    async (): Promise<FormResponsesReport> => {
      const { data } = await axios.get(formResponsesReportUrl(props));
      return data;
    },
    {
      enabled: !!props.template,
      keepPreviousData: true,
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
      onError: (error) => {
        // Template only: no client ids in the payload.
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useFormResponsesReport',
          data: { template: props.template },
        });
      },
    }
  );
}

function useFormResponsesReportTemplates(props: { providerUuid?: string; enabled: boolean }) {
  return useQuery(
    // The provider uuid keys the cache so switching between clinic and solo context refetches.
    ['form-responses-report-templates', props.providerUuid],
    async (): Promise<FormResponsesReportTemplate[]> => {
      const { data } = await axios.get(`${API_URL}/form-responses-report/templates/`);
      return data;
    },
    {
      enabled: props.enabled,
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
      onError: (error) => {
        monitoringCaptureError(error, {
          module: 'api',
          func: 'useFormResponsesReportTemplates',
          data: { providerUuid: props.providerUuid },
        });
      },
    }
  );
}

function downloadFormResponsesReport(props: FormResponsesReportParams, exportAs: 'csv' | 'xlsx'): Promise<void> {
  return axios
    .get(formResponsesReportUrl(props, exportAs), { responseType: 'blob' })
    .then((res) => {
      const match = /filename="(.+)"/.exec(res.headers['content-disposition'] || '');
      fileDownload(res.data, match ? match[1] : `form-responses.${exportAs}`);
    })
    .catch((error) => {
      monitoringCaptureError(error, {
        module: 'api',
        func: 'downloadFormResponsesReport',
        data: { template: props.template, exportAs },
      });
      throw error;
    });
}

function removeFormReportInvite(inviteUuid: string): Promise<void> {
  return axios.post(`${API_URL}/form-responses-report/remove-invite/`, { invite: inviteUuid }).then(() => undefined);
}

// Clinic-wide service plan report. Takes no parameters — the endpoint returns the
// clinic's full list and all filtering/sorting happens client-side.
function useServicePlanReport() {
  return useQuery(
    ['service-plan-report'],
    async (): Promise<ServicePlanRow[]> => {
      const { data } = await axios.get(`${API_URL}/service-plan-report/`);
      return data;
    },
    {
      // Plan status changes arrive out of band (Stripe webhooks), so this must
      // revalidate on every mount — a stale window would survive the polling
      // Task 5 does after an edit. cacheTime still paints instantly from cache.
      staleTime: 0,
      cacheTime: 1000 * 60 * 10,
      refetchOnWindowFocus: false, // clinic-wide query, too heavy to fire on every focus
      // A 403 here is the endpoint working as designed (non-clinic caller), not a
      // transient fault: never retry it, and don't report it as an error.
      retry: (count, error: any) => (error?.response?.status ?? 500) >= 500 && count < 2,
      onError: (error: any) => {
        if (error?.response?.status === 403) return;
        monitoringCaptureError(error, { module: 'api', func: 'useServicePlanReport' });
      },
    }
  );
}

// WST-1090: portal announcements, already filtered per user server-side.
export type Announcement = {
  id: number;
  title: string;
  body_html: string;
  severity: 'info' | 'warning' | 'danger';
};

export function useAnnouncements(enabled: boolean) {
  return useQuery(
    'announcements',
    async (): Promise<Announcement[]> => {
      const { data } = await axios.get(`${API_URL}/announcements/`);
      return data;
    },
    {
      enabled,
      // react-query refetches on window focus/visibilitychange only when data is older
      // than staleTime, which is how announcements reappear after the user returns from
      // several hours away; the fetch on mount covers app load.
      staleTime: 4 * 60 * 60 * 1000,
      refetchOnWindowFocus: true,
      onError: (error) => monitoringCaptureError(error, { module: 'api', func: 'useAnnouncements' }),
    }
  );
}

export function useDismissAnnouncement() {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, forever }: { id: number; forever: boolean }) =>
      axios.post(`${API_URL}/announcements/${id}/dismiss/`, { forever }),
    {
      onMutate: async ({ id }) => {
        await queryClient.cancelQueries('announcements');
        const previous = queryClient.getQueryData<Announcement[]>('announcements');
        queryClient.setQueryData<Announcement[]>('announcements', (old) => (old || []).filter((a) => a.id !== id));
        return { previous };
      },
      onError: (error, variables, context: { previous?: Announcement[] } | undefined) => {
        if (context?.previous) queryClient.setQueryData('announcements', context.previous);
        monitoringCaptureError(error, { module: 'api', func: 'useDismissAnnouncement', data: variables });
      },
    }
  );
}

export {
  useServices,
  useService,
  useProviderServices,
  getProviderServicesForProvider,
  getProviderServicesCount,
  setupProviderServices,
  getProviderService,
  useProviderService,
  getProviderServiceByProviderAndService,
  getProviderServiceByProviderAndParentProviderService,
  getProviderServiceInvite,
  getExistingClientBookingAccess,
  useExistingClientBookingAccess,
  updateProviderService,
  useUpdateProviderService,
  useCreateProviderService,
  useProviderTimeSlots,
  useUpdateProviderTimeSlot,
  useCreateProviderTimeSlot,
  useProviderTimeSlotServices,
  setupProviderTimeSlot,
  getProviderTimeSlotCount,
  getProviderByInvite,
  useProviderMe,
  useUpdateProvider,
  useClients,
  getClientsCount,
  getClientProvider,
  getClientProviderId,
  getClientProviderById,
  getClientProvidersCount,
  downloadClientProvidersCSV,
  useClientProviders,
  useGuardians,
  useProviders,
  useProvider,
  getProviderPaymentEnabled,
  useAges,
  useExpertises,
  useConcerns,
  useLanguages,
  usePostalCodes,
  getPostalCode,
  getZipTimezone,
  getPlaceTimezones,
  useTimeSlots,
  useUpdateTimeSlot,
  useCreateTimeSlot,
  getEvents,
  getEventsForSchedule,
  getEvent,
  useEvents,
  useEventsForSchedule,
  useSessionsReport,
  usePaymentsReport,
  useActivityReport,
  useFormResponsesReport,
  useFormResponsesReportTemplates,
  downloadFormResponsesReport,
  removeFormReportInvite,
  useServicePlanReport,
  getEventLog,
  getEventLogFromEvent,
  updateEvent,
  useUpdateEvent,
  useCreateEvent,
  importEvent,
  getEventPaymentStatus,
  getRelatedFutureEvents,
  getBatchEventPaymentStatus,
  updateEventSubscription,
  updateEventOneoff,
  useDeleteEvent,
  getRescheduleEvent,
  rescheduleEvent,
  useEventsLog,
  updateEventLog,
  createEventLog,
  useClientProvider,
  useUpdateClientProvider,
  getOnboarding,
  getPaymentResolution,
  retryPaymentResolution,
  confirmMethodPaymentResolution,
  useOnboarding,
  updateOnboarding,
  useAvailableTimeslot,
  availableTimeslotCount,
  useAvailableTimeslotHold,
  useAvailableTimeslotRelease,
  holdAvailableTimeslot,
  releaseAvailableTimeslot,
  bookAvailableTimeslots,
  existsFormTemplate,
  getFormTemplate,
  getFormTemplates,
  useFormTemplates,
  inviteFormTemplate,
  bulkInviteFormTemplate,
  createFormTemplate,
  useCreateFormTemplate,
  updateFormTemplate,
  useUpdateFormTemplate,
  deleteFormTemplate,
  useDeleteFormTemplate,
  generateFormTemplateFromFile,
  generateFormTemplateFromPrompt,
  getFormTemplateGenerateStatus,
  approveFormTemplateDraft,
  createForm,
  updateForm,
  getIntake,
  postIntake,
  getNote,
  useNotes,
  useCreateNote,
  useUpdateNote,
  useDocuments,
  getDocument,
  createDocument,
  useCreateDocument,
  updateDocument,
  useUpdateDocument,
  bulkShareDocument,
  getDocumentThumbnailUrl,
  downloadDocument,
  openDocument,
  deleteDocument,
  uploadImage,
  imageUrl,
  downloadInvoice,
  downloadInvoiceReceipt,
  downloadClientInvoices,
  useClinic,
  useIcsIntegrations,
  createIcsIntegration,
  updateIcsIntegration,
  deleteIcsIntegration,
  refreshIcsIntegration,
  ping,
  getInvoices,
  getStripeInvoices,
  useNoteTemplates,
  createNoteTemplate,
  updateNoteTemplate,
  deleteNoteTemplate,
  useExpenses,
  createExpense,
  updateExpense,
  deleteExpense,
  useSuperBills,
  getSuperBill,
  createSuperBill,
  deleteSuperBill,
  downloadSuperBill,
  downloadSuperBillPreview,
  guestPaymentSetup,
  clientPaymentSetup,
  useDatasets,
  getDatasets,
  useLocations,
  getLocations,
  updateLocation, // Added
  useUpdateLocation, // Added
  createLocation,
  useCreateLocation,
  deleteLocation,
  useDeleteLocation,
  updateClientNotifications,
  useUpdateClientNotifications,
  updateGuardianNotifications,
  useUpdateGuardianNotifications,
  updateProviderNotifications,
  useUpdateProviderNotifications,
  getServiceBundles,
  createServiceBundle,
  updateServiceBundle,
  useClientTags,
  createClientTag,
  updateClientTag,
  deleteClientTag,
};
